/* ==========================================================================
   wc2-color.js — カラーチャートの挙動
   --------------------------------------------------------------------------
   依存なし・素のJS。defer で読み込む。
   代表色は assets/wc2-color-reps.js(自動生成)が置く window.WC2_COLOR_REPS。

   安全上の約束(ここを崩さないこと)
     - お客様が置いた参考画像は、お使いの端末の中だけで扱う。
       このファイルには fetch / XMLHttpRequest / sendBeacon / WebSocket /
       Worker / form の送信を **1つも書かない**。
     - 参考画像を localStorage / sessionStorage / IndexedDB / Cache に
       **保存しない**。読み込んだ元データは表示用canvasへ描いた時点で捨てる
       (revokeObjectURL / bitmap.close())。
     - 計測(dataLayer)に送るのは、画像の内容とも拾った色の値とも関係のない
       「操作が起きた」という事実だけ。方式・点の番号・件数・色のhandleのみ。
     - localStorage に入れるのは候補の色名とhandleだけ。

   受け持つもの
     - 系統タブ(固定ヘッダーと貼りつくタブの高さを見込んで移動)
     - 色見本のその場拡大 / 前後送り(矢印キー・スワイプ)/ 色名コピー
     - 候補(最大5)/ 比較ビュー
     - 参考画像(ファイル・ドラッグ・貼り付け)の表示、拡大・移動、色を拾う
       拾う点は **1つだけ**。別の場所をタップすると、その点が新しい点に置き換わる。
     - 画面上で近い色(sRGB → Lab(D65) → CIEDE2000、上位5件)

   商品ページへのリンクは張らない(色見本から商品詳細へ行く導線は作らない)。
   ========================================================================== */
(function () {
  'use strict';

  var MAX_PICKS = 5;
  var MAX_SIDE = 1600; /* 参考画像の長辺の上限(px) */
  var STORE_KEY = 'wigcut_color_picks_v1';
  var PI = Math.PI;
  var D2R = PI / 180;

  /* ------------------------------------------------------------- 計測 */

  function track(event, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      var o = { event: event };
      if (payload) {
        for (var k in payload) {
          if (Object.prototype.hasOwnProperty.call(payload, k)) o[k] = payload[k];
        }
      }
      window.dataLayer.push(o);
    } catch (e) {
      /* 計測の失敗でページを止めない */
    }
  }

  /* --------------------------------------------- 色の計算(Lab / ΔE2000) */

  function toLab(rgb) {
    function inv(v) {
      v /= 255;
      return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    }
    var R = inv(rgb[0]);
    var G = inv(rgb[1]);
    var B = inv(rgb[2]);
    var X = (R * 0.4124564 + G * 0.3575761 + B * 0.1804375) / 0.95047;
    var Y = R * 0.2126729 + G * 0.7151522 + B * 0.072175;
    var Z = (R * 0.0193339 + G * 0.119192 + B * 0.9503041) / 1.08883;
    function f(t) {
      return t > 0.008856451679 ? Math.cbrt(t) : (903.2962962 * t + 16) / 116;
    }
    var fx = f(X);
    var fy = f(Y);
    var fz = f(Z);
    return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
  }

  function deltaE00(p, q) {
    var L1 = p[0], a1 = p[1], b1 = p[2];
    var L2 = q[0], a2 = q[1], b2 = q[2];
    var C1 = Math.sqrt(a1 * a1 + b1 * b1);
    var C2 = Math.sqrt(a2 * a2 + b2 * b2);
    var Cb = (C1 + C2) / 2;
    var Cb7 = Math.pow(Cb, 7);
    var G = 0.5 * (1 - Math.sqrt(Cb7 / (Cb7 + 6103515625)));
    var ap1 = (1 + G) * a1, ap2 = (1 + G) * a2;
    var Cp1 = Math.sqrt(ap1 * ap1 + b1 * b1);
    var Cp2 = Math.sqrt(ap2 * ap2 + b2 * b2);
    var hp1 = b1 === 0 && ap1 === 0 ? 0 : Math.atan2(b1, ap1);
    if (hp1 < 0) hp1 += 2 * PI;
    var hp2 = b2 === 0 && ap2 === 0 ? 0 : Math.atan2(b2, ap2);
    if (hp2 < 0) hp2 += 2 * PI;
    var dL = L2 - L1, dC = Cp2 - Cp1, dhp = 0;
    if (Cp1 * Cp2 !== 0) {
      dhp = hp2 - hp1;
      if (dhp > PI) dhp -= 2 * PI;
      else if (dhp < -PI) dhp += 2 * PI;
    }
    var dH = 2 * Math.sqrt(Cp1 * Cp2) * Math.sin(dhp / 2);
    var Lb = (L1 + L2) / 2, Cpb = (Cp1 + Cp2) / 2, hpb;
    if (Cp1 * Cp2 === 0) {
      hpb = hp1 + hp2;
    } else if (Math.abs(hp1 - hp2) > PI) {
      hpb = (hp1 + hp2 + 2 * PI) / 2;
      if (hp1 + hp2 >= 2 * PI) hpb = (hp1 + hp2 - 2 * PI) / 2;
    } else {
      hpb = (hp1 + hp2) / 2;
    }
    var T =
      1 - 0.17 * Math.cos(hpb - 30 * D2R) + 0.24 * Math.cos(2 * hpb) +
      0.32 * Math.cos(3 * hpb + 6 * D2R) - 0.2 * Math.cos(4 * hpb - 63 * D2R);
    var dTh = 30 * D2R * Math.exp(-Math.pow((hpb / D2R - 275) / 25, 2));
    var Cpb7 = Math.pow(Cpb, 7);
    var Rc = 2 * Math.sqrt(Cpb7 / (Cpb7 + 6103515625));
    var Sl = 1 + (0.015 * Math.pow(Lb - 50, 2)) / Math.sqrt(20 + Math.pow(Lb - 50, 2));
    var Sc = 1 + 0.045 * Cpb;
    var Sh = 1 + 0.015 * Cpb * T;
    var Rt = -Math.sin(2 * dTh) * Rc;
    var tL = dL / Sl, tC = dC / Sc, tH = dH / Sh;
    return Math.sqrt(tL * tL + tC * tC + tH * tH + Rt * tC * tH);
  }

  /* 近さは3段階の言葉で出す。ΔEの数値は出さない(数値は根拠として重く見えすぎる)。
     3段階のいちばん外(やや近い)の範囲も超えて離れている色には、**言葉を付けない**
     (色名だけにする)。離れすぎた色を「近い」と呼ばないため。
     「他の候補を表示」で6位以降まで出せるようになり、離れた色も並ぶので入れた歯止め。 */
  var FAR_DE = 20;
  function nearWord(d) {
    if (d >= FAR_DE) return '';
    return d < 4 ? 'かなり近い' : d < 10 ? '近い' : 'やや近い';
  }

  function rgbToCss(c) {
    return 'rgb(' + Math.round(c[0]) + ',' + Math.round(c[1]) + ',' + Math.round(c[2]) + ')';
  }

  /* 代表色の計算方法。docs/tools/build_color_reps.py と同じ:
     中央50%の矩形 → 輝度の下位15%(影)と上位15%(光沢)を捨てる → R/G/B の中央値 */
  function representativeFromImageData(data, w, h) {
    var cw = Math.max(1, Math.floor(w * 0.5));
    var ch = Math.max(1, Math.floor(h * 0.5));
    var x0 = Math.floor((w - cw) / 2);
    var y0 = Math.floor((h - ch) / 2);
    var rows = [];
    for (var y = y0; y < y0 + ch; y++) {
      for (var x = x0; x < x0 + cw; x++) {
        var i = (y * w + x) * 4;
        var r = data[i], g = data[i + 1], b = data[i + 2];
        rows.push([0.2126 * r + 0.7152 * g + 0.0722 * b, r, g, b]);
      }
    }
    if (!rows.length) return null;
    rows.sort(function (a, b) { return a[0] - b[0]; });
    var n = rows.length;
    var core = rows.slice(Math.floor(n * 0.15), n - Math.floor(n * 0.15));
    if (!core.length) core = rows;
    function median(idx) {
      var v = core.map(function (c) { return c[idx]; }).sort(function (a, b) { return a - b; });
      var m = Math.floor(v.length / 2);
      return v.length % 2 ? v[m] : Math.round((v[m - 1] + v[m]) / 2);
    }
    return [median(1), median(2), median(3)];
  }

  /* ====================================================================== */

  function init(root) {
    if (!root || root.getAttribute('data-wc2c-bound') === '1') return;
    root.setAttribute('data-wc2c-bound', '1');

    var $ = function (sel) { return root.querySelector(sel); };
    var $$ = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };

    var reduce = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : { matches: false };

    /* スマホ(749px以下)か。CSS の分かれ目と必ず同じ値にする。
       スマホでは「1画面に1つのこと」を守るため、参考画像の貼りつく小窓と
       候補トレイをやめ、全画面のシートと低いバーに置き換える。 */
    var mqMobile = window.matchMedia
      ? window.matchMedia('(max-width: 749px)')
      : { matches: false, addListener: function () {} };
    function isMobile() { return !!mqMobile.matches; }

    /* サイト共通の追従LINEボタンを、このページのスマホでだけ出さないための目印。
       CSS 側は :has(.wc2c) でも同じことをしているので、:has が効く環境では
       JSを待たずに消える(一瞬だけ出るチラつきが起きない)。 */
    try { document.body.classList.add('wc2c-page'); } catch (e) {}

    /* ------------------------------------------------ 色のデータ(DOMから) */

    var reps = (window.WC2_COLOR_REPS && window.WC2_COLOR_REPS.colors) || {};
    var items = $$('.wc2c-sw').map(function (li) {
      return {
        el: li,
        i: parseInt(li.getAttribute('data-wc2c-i'), 10),
        h: li.getAttribute('data-wc2c-h') || '',
        n: li.getAttribute('data-wc2c-n') || '',
        f: li.getAttribute('data-wc2c-f') || '',
        d: li.getAttribute('data-wc2c-d') || '',
        b: li.getAttribute('data-wc2c-b') || '',
        /* その色の商品に登録されている写真のURL(1枚目を含む。空白区切り)。
           1枚目と同じフォルダのものは共通部分が省いてあるので、ここでは文字列のまま
           持っておき、拡大を開いたときに photoUrl() で組み立てる。
           サムネイルの画像はここでは1枚も読み込まない。 */
        ph: (li.getAttribute('data-wc2c-ph') || '').split(' ').filter(function (s) { return !!s; }),
        rep: reps[li.getAttribute('data-wc2c-h')] || null,
        lab: null,
        img: li.querySelector('img')
      };
    });
    items.sort(function (a, b) { return a.i - b.i; });

    var byName = {};
    items.forEach(function (it) { byName[it.n] = it; });

    /* Lab への変換は重いので、最初に色を拾ったときにまとめて作る */
    var labReady = false;
    function ensureLab() {
      if (labReady) return;
      labReady = true;
      items.forEach(function (it) {
        if (it.rep && !it.lab) it.lab = toLab(it.rep);
      });
    }

    /* 代表色のデータに無い色(あとから商品が追加された場合)だけ、
       実行時に canvas で同じ方法により計算する。
       CORS で読めなければ提案の対象から外す(表示・拡大・候補は通常どおり)。 */
    var runtimeTried = false;
    function tryRuntimeReps(done) {
      if (runtimeTried) { done(); return; }
      runtimeTried = true;
      var missing = items.filter(function (it) { return !it.rep && it.b; });
      if (!missing.length) { done(); return; }
      var left = missing.length;
      var finish = function () { left--; if (left <= 0) { labReady = false; ensureLab(); done(); } };
      missing.forEach(function (it) {
        var im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = function () {
          try {
            var c = document.createElement('canvas');
            c.width = 128;
            c.height = Math.max(1, Math.round((im.naturalHeight / im.naturalWidth) * 128));
            var ctx = c.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(im, 0, 0, c.width, c.height);
            var d = ctx.getImageData(0, 0, c.width, c.height).data;
            var rep = representativeFromImageData(d, c.width, c.height);
            if (rep) { it.rep = rep; it.lab = toLab(rep); }
          } catch (e) {
            /* 読めなければ提案の対象から外す */
          }
          finish();
        };
        im.onerror = finish;
        im.src = it.b;
      });
    }

    /* ------------------------------------------------ 貼りつく高さの実測 */

    var HEADER_SEL = '.shopify-section-group-header-group, sticky-header, .section-header';
    var tabsEl = $('[data-wc2c-tabs]');
    var lastH = -1;
    var lastT = -1;

    function stickyHeaderHeight() {
      var best = 0;
      var els = document.querySelectorAll(HEADER_SEL);
      for (var i = 0; i < els.length; i++) {
        var cs = getComputedStyle(els[i]);
        if (cs.position !== 'sticky' && cs.position !== 'fixed') continue;
        var top = parseFloat(cs.top);
        if (isNaN(top) || top > 8) continue;
        var h = els[i].getBoundingClientRect().height;
        if (h > best) best = h;
      }
      return Math.round(best);
    }

    function measure() {
      var h = stickyHeaderHeight();
      var t = tabsEl ? Math.round(tabsEl.getBoundingClientRect().height) : 0;
      if (h !== lastH) { lastH = h; root.style.setProperty('--wc2c-header-h', h + 'px'); }
      if (t !== lastT) { lastT = t; root.style.setProperty('--wc2c-tabs-h', t + 'px'); }
    }

    function anchorOffset() {
      return (lastH < 0 ? 0 : lastH) + (lastT < 0 ? 0 : lastT) + 24;
    }

    function afterLayout(fn) {
      var done = false;
      var run = function () { if (done) return; done = true; fn(); };
      requestAnimationFrame(function () { requestAnimationFrame(run); });
      setTimeout(run, 100);
    }

    /* 位置を瞬時に合わせる。
       WebKit(iPhone の Safari / Chrome)では、背面の固定(position:fixed)を外した直後の
       window.scrollTo が効かないことがあるので、scrollingElement への直接代入も併せて行う。 */
    function setScrollTop(y) {
      y = Math.max(0, Math.round(y));
      window.scrollTo(0, y);
      var se = document.scrollingElement || document.documentElement;
      if (se && Math.abs(se.scrollTop - y) > 1) se.scrollTop = y;
    }

    /* 着地させて、そのあと本当にそこに居るかを確かめる。

       目的位置は毎回測り直す(参考画像の1行が入るなど、あとからレイアウトが動くため)。
       targetFn が null を返したら何もしない。
       利用者が自分で動かしたら(touchstart / wheel / keydown)、そこで手を引く。 */
    function landAt(targetFn, instant) {
      var want = targetFn();
      if (want === null) return;
      if (Math.abs(want - window.pageYOffset) <= 4) return;
      var live = true;
      var stop = function () {
        if (!live) return;
        live = false;
        ['touchstart', 'wheel', 'keydown'].forEach(function (t) {
          window.removeEventListener(t, stop);
        });
      };
      ['touchstart', 'wheel', 'keydown'].forEach(function (t) {
        window.addEventListener(t, stop, { passive: true });
      });
      var fast = instant || reduce.matches;
      if (fast) setScrollTop(want);
      else window.scrollTo({ top: want, behavior: 'smooth' });
      var check = function () {
        if (!live) return;
        var again = targetFn();
        if (again !== null && Math.abs(again - window.pageYOffset) > 4) setScrollTop(again);
      };
      /* なめらかな移動は300msではまだ途中なので、確かめるのは800msだけ */
      if (fast) setTimeout(check, 300);
      setTimeout(function () { check(); stop(); }, 800);
    }

    function goTo(el) {
      if (!el) return;
      afterLayout(function () {
        measure();
        var top = el.getBoundingClientRect().top + window.pageYOffset - anchorOffset();
        window.scrollTo({ top: Math.max(0, top), behavior: reduce.matches ? 'auto' : 'smooth' });
      });
    }

    measure();
    window.addEventListener('resize', function () {
      placeSortNote();
      placePhotos();
      measure();
      if (view.canvas) fitClampOnResize();
      drawRef();
      drawRefThumb();
      syncTray();
    });
    window.addEventListener('scroll', measure, { passive: true });

    /* ---------------------------------------------------------- 系統タブ */

    var famEls = $$('.wc2c-fam');

    if (tabsEl) {
      tabsEl.addEventListener('click', function (e) {
        var b = e.target.closest('[data-wc2c-go]');
        if (!b) return;
        setActiveTab(b.getAttribute('data-wc2c-go'));
        goTo(document.getElementById(b.getAttribute('data-wc2c-go')));
      });
    }

    var familyMenu = $('[data-wc2c-family-menu]');
    if (familyMenu) familyMenu.addEventListener('click', function (e) {
      var b = e.target.closest('[data-wc2c-go]');
      if (!b) return;
      setActiveTab(b.getAttribute('data-wc2c-go'));
      goTo(document.getElementById(b.getAttribute('data-wc2c-go')));
    });

    function setActiveTab(id) {
      var on = null;
      $$('.wc2c-tab').forEach(function (t) {
        var is = t.getAttribute('data-wc2c-go') === id;
        t.setAttribute('aria-current', is ? 'true' : 'false');
        if (is) on = t;
      });
      scrollTabIntoView(on);
    }

    /* 選択中のタブが横スクロールの外に出ていたら、見える位置まで寄せる。
       ページ自体は動かさない(帯の scrollLeft だけを変える)。 */
    function scrollTabIntoView(tab) {
      var inner = $('.wc2c-tabs__inner');
      if (!inner || !tab) return;
      var ir = inner.getBoundingClientRect();
      var tr = tab.getBoundingClientRect();
      var pad = 16;
      if (tr.left < ir.left + pad) inner.scrollLeft -= ir.left + pad - tr.left;
      else if (tr.right > ir.right - pad) inner.scrollLeft += tr.right - (ir.right - pad);
    }
    if (famEls.length) setActiveTab(famEls[0].id);

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) setActiveTab(en.target.id);
          });
        },
        { rootMargin: '-30% 0px -60% 0px' }
      );
      famEls.forEach(function (el) { io.observe(el); });
    }

    /* 色系統の個別一覧(collection テンプレート)から開いたときは、
       その系統のタブを選択状態にして、その位置へ移動する */
    var activeHandle = root.getAttribute('data-wc2c-active') || '';
    if (activeHandle) {
      var target = famEls.filter(function (el) {
        return el.getAttribute('data-wc2c-handle') === activeHandle;
      })[0];
      if (target) {
        setActiveTab(target.id);
        afterLayout(function () {
          measure();
          var top = target.getBoundingClientRect().top + window.pageYOffset - anchorOffset();
          window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
        });
      }
    }

    /* ------------------------------ 並べ替え(拾った色に近い順)

       色を拾って提案が出ているあいだは、**系統のセクションの順**も、
       **各系統の中の色見本の順**も「拾った色に近い順」にする。
       そうしないと、ピンクを拾っても提案の下に必ずイエロー系が来るし、
       系統の中もオレンジ01から始まってピンクが下のほうに埋もれる。

       系統の順位づけ: その系統に属する色のうち**最も近い色のΔE**で昇順。
       系統の中の順位づけ: 拾った色との**ΔE**で昇順。
       どちらも同点は元の順、代表色が無い色は末尾。距離は現行の CIEDE2000(kL=1)のまま。

       並べ替えは CSS の order ではなく **DOM の順序**を入れ替える。
       order だけだとキーボードの移動順・読み上げ順が見た目とずれるため。 */

    var famsWrapEl = $('.wc2c-families');
    var tabsInnerEl = $('.wc2c-tabs__inner');
    var sortNoteEl = $('[data-wc2c-sortnote]');
    var sortNoteOnEl = $('[data-wc2c-sortnote-on]');
    var sortNoteOffEl = $('[data-wc2c-sortnote-off]');
    var sortNoteBtnEl = $('[data-wc2c-sort-toggle]');
    var FAR_RANK = 1e9; /* 代表色が1つも無い系統は末尾へ(Infinity は比較でNaNになる) */

    var famInfo = famEls.map(function (el) {
      return {
        el: el,
        ul: el.querySelector('.wc2c-swatches'),
        tab: tabsInnerEl ? tabsInnerEl.querySelector('[data-wc2c-go="' + el.id + '"]') : null,
        /* 元の並び(商品の並び)。「元の並びに戻す」で必ずここへ戻る */
        items: items.filter(function (it) { return el.contains(it.el); })
      };
    });

    var sortEnabled = true; /* 「元の並びに戻す」を押している間だけ false */
    var sortApplied = false;

    /* タブの文字の前に、その系統の色の小さな丸を3つ添える。
       どの系統かが言葉より先に色で分かるようにするため。
       代表色を明るさの順に並べ、暗い / 中間 / 明るい の3つを選ぶ。
       装飾なので aria-hidden。代表色が1つも無い系統には付けない。 */
    function buildTabDots() {
      famInfo.forEach(function (f) {
        if (!f.tab || f.tab.querySelector('.wc2c-tab__dots')) return;
        var cols = f.items
          .map(function (it) { return it.rep; })
          .filter(function (c) { return !!c; })
          .map(function (c) { return { c: c, l: 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2] }; })
          .sort(function (a, b) { return a.l - b.l; });
        if (!cols.length) return;
        var n = cols.length;
        var three = [cols[n - 1].c, cols[Math.floor(n / 2)].c, cols[0].c];
        var box = document.createElement('span');
        box.className = 'wc2c-tab__dots';
        box.setAttribute('aria-hidden', 'true');
        three.forEach(function (c) {
          var d = document.createElement('i');
          d.style.background = rgbToCss(c);
          box.appendChild(d);
        });
        f.tab.insertBefore(box, f.tab.firstChild);
      });
    }
    buildTabDots();

    /* スマホでは、系統の並べ替えの案内を貼りつくタブ帯から出して、
       提案の下(1つ目の系統の見出しの上)に貼りつかない形で置く。
       タブ帯を常に1行・52〜56pxに保つため。PCは従来どおりタブ帯の中。 */
    var sortSlotEl = $('[data-wc2c-sortslot]');
    function placeSortNote() {
      if (!sortNoteEl || !sortSlotEl || !tabsEl) return;
      var parent = isMobile() ? sortSlotEl : tabsEl;
      if (sortNoteEl.parentNode !== parent) {
        parent.appendChild(sortNoteEl);
        measure();
      }
    }
    placeSortNote();

    function rankFams(lab) {
      return famInfo
        .map(function (f, idx) {
          var best = FAR_RANK;
          f.items.forEach(function (it) {
            if (!it.lab) return;
            var d = deltaE00(lab, it.lab);
            if (d < best) best = d;
          });
          return { f: f, d: best, idx: idx };
        })
        .sort(function (a, b) { return a.d - b.d || a.idx - b.idx; })
        .map(function (r) { return r.f; });
    }

    /* 生成りの帯(--alt)は元は Liquid の cycle で1つおきに付けている。
       並べ替えたあとも1つおきになるよう、並びに合わせて付け直す。 */
    function paintFamAlt(list) {
      list.forEach(function (f, i) { f.el.classList.toggle('wc2c-fam--alt', i % 2 === 1); });
    }

    /* 系統の中の色見本の並び。lab があれば「拾った色に近い順」、null なら元の並び。
       同じ距離のときは元の順。代表色が無い色(提案の対象外)は末尾へ元の順で。 */
    function orderSwatches(f, lab) {
      if (!lab) return f.items;
      return f.items
        .map(function (it, idx) {
          return { it: it, idx: idx, d: it.lab ? deltaE00(lab, it.lab) : FAR_RANK };
        })
        .sort(function (a, b) { return a.d - b.d || a.idx - b.idx; })
        .map(function (r) { return r.it; });
    }

    /* 全系統の色見本を並べ替える。170個を動かすので、
       系統ごとに DocumentFragment へまとめてから1回で戻す(挿入は系統あたり1回)。
       並びが変わらない系統には触らない。 */
    function applySwatchOrder(lab) {
      famInfo.forEach(function (f) {
        if (!f.ul) return;
        var want = orderSwatches(f, lab);
        var now = Array.prototype.filter.call(f.ul.children, function (el) {
          return el.classList && el.classList.contains('wc2c-sw');
        });
        var same = now.length === want.length &&
          want.every(function (it, i) { return now[i] === it.el; });
        if (same) return;
        var frag = document.createDocumentFragment();
        want.forEach(function (it) { frag.appendChild(it.el); });
        f.ul.appendChild(frag);
      });
    }

    /* 系統の順と、系統の中の色の順を、まとめて入れ替える。
       lab が null なら両方とも元の並びに戻す。 */
    function applyFamOrder(list, lab) {
      if (!famsWrapEl || !list.length) return;
      /* 提案を見ている位置を保つ。DOMを動かしてもページの高さは変わらないが、
         フォーカスの復帰などで動くことがあるので、前後で実測して戻す。 */
      var y = window.pageYOffset;
      var focused = document.activeElement;
      var keepFocus = focused && focused !== document.body && focused.focus &&
        list.some(function (f) { return f.el.contains(focused) || (f.tab && f.tab.contains(focused)); });
      var now = Array.prototype.filter.call(famsWrapEl.children, function (el) {
        return el.classList && el.classList.contains('wc2c-fam');
      });
      var sameFam = now.length === list.length && list.every(function (f, i) { return now[i] === f.el; });
      if (!sameFam) {
        var frag = document.createDocumentFragment();
        list.forEach(function (f) { frag.appendChild(f.el); });
        famsWrapEl.appendChild(frag);
        if (tabsInnerEl) {
          var tfrag = document.createDocumentFragment();
          list.forEach(function (f) { if (f.tab) tfrag.appendChild(f.tab); });
          tabsInnerEl.appendChild(tfrag);
        }
      }
      paintFamAlt(list);
      applySwatchOrder(lab);
      if (keepFocus && document.activeElement !== focused) {
        try { focused.focus({ preventScroll: true }); } catch (e) { focused.focus(); }
      }
      if (Math.round(window.pageYOffset) !== Math.round(y)) window.scrollTo(0, y);
    }

    function currentPickLab() {
      return ref.pt ? ref.pt.lab : null;
    }

    function syncFamOrder() {
      var on = !!(ref.src && ref.pt) && sortEnabled;
      var lab = on ? currentPickLab() : null;
      if (lab) {
        var list = rankFams(lab);
        applyFamOrder(list, lab);
        sortApplied = true;
        setActiveTab(list[0].el.id);
      } else if (sortApplied) {
        applyFamOrder(famInfo.slice(), null);
        sortApplied = false;
        setActiveTab(famInfo[0].el.id);
      }
      syncSortNote();
    }

    /* 案内の2つの文言は同じマスに重ねてあり、使わないほうを visibility:hidden にする。
       帯の高さは長いほうの文言で決まるので、並べ替えの有無で高さが変わらない。
       (以前は文言の入れ替えで 375px のとき帯が 24px ⇄ 43px と動き、その分ページが
       上下にずれて window.scrollY が ±19.5px 動いていた。) */
    function syncSortNote() {
      if (!sortNoteEl) return;
      var has = !!(ref.src && ref.pt);
      var was = sortNoteEl.hidden;
      sortNoteEl.hidden = !has;
      if (has) {
        if (sortNoteOnEl) {
          sortNoteOnEl.classList.toggle('is-off', !sortEnabled);
          sortNoteOnEl.setAttribute('aria-hidden', sortEnabled ? 'false' : 'true');
        }
        if (sortNoteOffEl) {
          sortNoteOffEl.classList.toggle('is-off', sortEnabled);
          sortNoteOffEl.setAttribute('aria-hidden', sortEnabled ? 'true' : 'false');
        }
        if (sortNoteBtnEl) sortNoteBtnEl.textContent = sortEnabled ? '元の並びに戻す' : '近い順に並べる';
      }
      /* 出る / 消えるときだけ貼りつくタブ帯の高さが変わるので、測り直す */
      if (was !== sortNoteEl.hidden) measure();
    }

    bindClick('[data-wc2c-sort-toggle]', function () {
      sortEnabled = !sortEnabled;
      syncFamOrder();
      say(sortEnabled ? '拾った色に近い順に並べました' : '元の並びに戻しました');
    });

    /* ============================ シートの作法(3種類で共通)

       全画面のシートは3種類ある(色を拾う / 色を大きく見る / 候補を見比べる)。
       どれも同じ作法で開閉する:
         - role="dialog" aria-modal="true"(Liquid 側)
         - 背面のスクロールを止める(iOS Safari でも動かないよう position:fixed 方式。
           スクロール位置は保持して、閉じたときに戻す)
         - Esc・閉じるボタン・端末の戻る操作で閉じる
         - フォーカストラップ。閉じたら元の要素へ preventScroll で戻す
       端末の戻るで閉じるために history.pushState を1つ積む。押されると popstate が
       来るので、そこで実際に閉じる。ページ自体は戻らない。

       閉じるときの順番は closeTop() / finishClose() に1本化してある。
       ばらばらに走らせると、ブラウザのスクロール復元・固定の解除・着地の移動が
       互いを打ち消す(実機で起きた不具合。docs/color-chart-implementation.md 17章)。 */

    var modalStack = [];
    var histCount = 0;
    var scrollLockY = 0;
    var lockCount = 0;
    var closingNow = false;  /* 閉じる流れが走っているあいだ。二重に走らせない */
    var selfBack = null;     /* 自分で呼んだ history.back() の popstate を見分ける */
    var backTimedOut = 0;    /* 待ちきれずに先へ進んだ時刻。遅れて来た popstate を捨てる */
    var landFn = null;       /* 閉じたあとの着地先(確定して閉じたときだけ入る) */

    /* ブラウザのスクロール復元を止める(このページにいるあいだだけ)。

       端末の戻るで閉じるために history.pushState を積んでいるので、閉じるときの popstate で
       ブラウザが「前にいた位置」を勝手に戻す(scrollRestoration = 'auto' の既定の動き)。
       これが、こちらの「提案の先頭へ移動」の**あとに**効いて打ち消していた。
       iPhone の Chrome / Safari はどちらも WebKit で、この復元が popstate より遅れて来る。
       シートを初めて開くときに、いまの履歴エントリだけ 'manual' にする。
       ページを離れるときは元に戻すので、別ページから戻ってきたときの復元は今までどおり。 */
    var scrollRestoreOrig = null;
    function holdScrollRestore() {
      if (scrollRestoreOrig !== null || !('scrollRestoration' in history)) return;
      try {
        scrollRestoreOrig = history.scrollRestoration;
        history.scrollRestoration = 'manual';
      } catch (e) { scrollRestoreOrig = null; }
    }
    function releaseScrollRestore() {
      if (scrollRestoreOrig === null) return;
      try { history.scrollRestoration = scrollRestoreOrig; } catch (e) {}
      scrollRestoreOrig = null;
    }
    window.addEventListener('pagehide', releaseScrollRestore);

    function lockScroll() {
      if (lockCount++ > 0) return;
      scrollLockY = window.pageYOffset;
      document.body.style.top = -scrollLockY + 'px';
      document.body.classList.add('wc2c-lock');
    }
    /* keepY = true のときは、開く前の位置へ戻さない
       (別の着地先が決まっているとき。戻してから動かすと、遠くの位置が一瞬見える) */
    function unlockScroll(keepY) {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount > 0) return;
      document.body.classList.remove('wc2c-lock');
      document.body.style.top = '';
      if (!keepY) setScrollTop(scrollLockY);
    }

    function modalOpen(cfg) {
      cfg.restore = document.activeElement;
      modalStack.push(cfg);
      cfg.ov.hidden = false;
      cfg.ov.classList.add('is-open');
      lockScroll();
      hideFab(true);
      cfg.hist = false;
      holdScrollRestore();
      try {
        history.pushState({ wc2cSheet: modalStack.length }, '');
        histCount++;
        cfg.hist = true;
      } catch (e) {}
      if (cfg.focus && cfg.focus.focus) {
        try { cfg.focus.focus({ preventScroll: true }); } catch (e2) { cfg.focus.focus(); }
      }
      return cfg;
    }

    /* 閉じる流れ(1本道。順番が命)

       1. シートを隠す
       2. 履歴の後始末。自分で閉じたときだけ history.back() を呼び、popstate を**待つ**
          (WebKit では遅れて来る・来ないことがあるので、待つのは最大400ms)
       3. 背面の固定を解除して、開く前の位置へ戻す
       4. 後処理(onClose)。ここで着地先(landFn)が決まることがある
       5. rAF×2(または100ms)でレイアウトが決まってから着地し、300ms / 800ms 後に確かめる

       useHist = false は端末の戻る(popstate)から来た場合。履歴はもう戻っているので触らない。 */
    function closeTop(useHist) {
      var cfg = modalStack[modalStack.length - 1];
      if (!cfg || closingNow) return;
      closingNow = true;
      modalStack.pop();
      cfg.ov.classList.remove('is-open');
      cfg.ov.hidden = true;
      if (useHist) {
        popOneHistory(cfg, function () { finishClose(cfg); });
      } else {
        if (cfg.hist) histCount = Math.max(0, histCount - 1);
        finishClose(cfg);
      }
    }

    function popOneHistory(cfg, next) {
      if (!cfg.hist || histCount <= 0) { next(); return; }
      histCount = Math.max(0, histCount - 1);
      var done = false;
      var timer = null;
      var go = function (timedOut) {
        if (done) return;
        done = true;
        selfBack = null;
        if (timedOut) backTimedOut = Date.now();
        if (timer) clearTimeout(timer);
        next();
      };
      selfBack = function () { go(false); };
      timer = setTimeout(function () { go(true); }, 400);
      try { history.back(); } catch (e) { go(false); }
    }

    function finishClose(cfg) {
      /* 着地先は閉じる前に決めてある(「この色に近い色を見る」のときだけ入っている) */
      var land = landFn;
      landFn = null;
      unlockScroll(!!land);
      /* 固定を外した直後に、いまわかる範囲の着地先へ瞬時に置く。
         このあと参考画像の1行が入って少しずれるので、最後にもう一度そろえる */
      if (land) {
        var rough = land();
        if (rough !== null) setScrollTop(rough);
      }
      if (!modalStack.length) hideFab(false);
      syncTray();
      if (cfg.onClose) cfg.onClose(cfg);
      var r = cfg.restore;
      if (r && r.focus && document.contains(r)) {
        try { r.focus({ preventScroll: true }); } catch (e) { r.focus(); }
      }
      closingNow = false;
      /* まだ別のシートが開いている(重ねて開いた)なら、背面は固定のまま。動かさない */
      if (lockCount > 0) return;
      var backY = scrollLockY;
      var target = land || function () { return backY; };
      afterLayout(function () {
        measure();
        landAt(target, true);
      });
    }

    /* 閉じるボタン・Esc・下スワイプから */
    function modalClose() { closeTop(true); }

    window.addEventListener('popstate', function () {
      if (selfBack) { var go = selfBack; selfBack = null; go(); return; }
      /* 待ちきれずに先へ進んだあとで遅れて来た popstate は、もう用が済んでいる */
      if (backTimedOut && Date.now() - backTimedOut < 1200) { backTimedOut = 0; return; }
      /* 端末の戻る。履歴はもう戻っているので、こちらからは back を呼ばない */
      if (modalStack.length) closeTop(false);
    });

    /* 再読み込み・別ページから戻ってきたときに、背面が固定されたまま・
       スクロールできないままにならないようにする(bfcache からの復帰) */
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      selfBack = null;
      backTimedOut = 0;
      closingNow = false;
      landFn = null;
      while (modalStack.length) {
        var cfg = modalStack.pop();
        cfg.ov.classList.remove('is-open');
        cfg.ov.hidden = true;
        if (cfg.onClose) cfg.onClose(cfg);
      }
      histCount = 0;
      lockCount = 0;
      document.body.classList.remove('wc2c-lock');
      document.body.style.top = '';
      hideFab(false);
      syncTray();
    });

    /* シートの見出しの帯を下へ払うと閉じる(本文のスクロールとぶつからない位置) */
    function bindSwipeClose(bar) {
      if (!bar) return;
      var y0 = null;
      bar.addEventListener('touchstart', function (e) { y0 = e.touches[0].clientY; }, { passive: true });
      bar.addEventListener('touchend', function (e) {
        if (y0 === null) return;
        var dy = e.changedTouches[0].clientY - y0;
        y0 = null;
        if (dy > 60) modalClose();
      }, { passive: true });
    }

    /* ------------------------------------------------- トースト / 読み上げ */

    var toastEl = $('[data-wc2c-toast]');
    var liveEl = $('[data-wc2c-live]');
    var toastTimer = null;

    /* act を渡すと「元に戻す」を数秒だけ出す({ label, fn })。
       確認ダイアログの代わりなので、押せるのはこのときだけ(CSS の .has-act)。 */
    function toast(msg, act) {
      if (!toastEl) return;
      toastEl.textContent = '';
      toastEl.classList.toggle('has-act', !!act);
      var text = document.createElement('span');
      text.textContent = msg;
      toastEl.appendChild(text);
      if (act) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'wc2c-toast__undo';
        b.textContent = act.label;
        b.addEventListener('click', function () {
          hideToast();
          act.fn();
        });
        toastEl.appendChild(b);
      }
      toastEl.classList.add('is-on');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(hideToast, act ? 6000 : 2800);
    }
    function hideToast() {
      if (!toastEl) return;
      clearTimeout(toastTimer);
      toastEl.classList.remove('is-on');
      toastEl.classList.remove('has-act');
    }
    function say(msg) { if (liveEl) liveEl.textContent = msg; }

    /* ------------------------------------------------------------ コピー */

    var COPY_NG = 'コピーできませんでした。名前を選んでコピーしてください';

    /* onOk を渡すと、トーストの代わりに結果(true/false)を返す。
       全画面のシートの中では、下に固定した主ボタンにトーストが重なるため、
       シートの中に書き出す側(候補を見比べる)がこちらを使う。 */
    function copy(text, msg, onOk) {
      function ok() { if (onOk) onOk(true); else toast(msg); }
      function ng() { if (onOk) onOk(false); else toast(COPY_NG); }
      function fallback() {
        try {
          var ta = document.createElement('textarea');
          ta.value = text;
          ta.setAttribute('readonly', '');
          ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
          document.body.appendChild(ta);
          ta.select();
          var done = document.execCommand('copy');
          document.body.removeChild(ta);
          if (done) ok();
          else ng();
        } catch (e) {
          ng();
        }
      }
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(ok, fallback);
      } else {
        fallback();
      }
    }

    function copiedMsg() {
      return ref.src
        ? 'コピーしました。LINEにそのまま貼れます。参考画像も、LINEで一緒にお送りください'
        : 'コピーしました。LINEにそのまま貼れます';
    }

    /* ============================================ 参考画像(端末の中だけ) */

    var ref = {
      src: null,   /* 表示用に縮小した canvas。以降の色の取得はここから行う */
      sctx: null,
      scale: 1,
      base: 1,
      tx: 0,
      ty: 0,
      pt: null,    /* {x, y, rgb, lab} 拾った点。1つだけ持つ(タップのたびに置き換え) */
      cur: null,
      kb: false
    };

    var panel = $('[data-wc2c-refpanel]');
    var stageEl = $('[data-wc2c-refstage]');
    var canvasEl = $('[data-wc2c-refcanvas]');
    var tipEl = $('[data-wc2c-reftip]');
    var chipsEl = $('[data-wc2c-chips]');
    var errEl = $('[data-wc2c-err]');
    var fileEl = $('[data-wc2c-file]');
    var dropEl = $('[data-wc2c-drop]');

    /* 参考画像を出す面は2つある。
         PC   : 左に貼りつく参考画像パネル(従来どおり)
         スマホ: 全画面の「色を拾う」シート
       いま生きているほうを view に入れて、描画・拡大・タップの計算はすべて
       view を見て行う。開いていないときは view.canvas = null(描かない)。 */
    var pickStageEl = $('[data-wc2c-pickstage]');
    var pickCanvasEl = $('[data-wc2c-pickcanvas]');
    var view = { stage: null, canvas: null };

    function setView(stage, canvas) {
      view.stage = stage || null;
      view.canvas = canvas || null;
    }
    if (!isMobile()) setView(stageEl, canvasEl);

    function showErr(m) {
      if (!errEl) return;
      if (!m) { errEl.hidden = true; errEl.textContent = ''; return; }
      errEl.hidden = false;
      errEl.textContent = m;
    }

    function fromFile(file, how) {
      showErr('');
      if (!file) return;
      if (window.createImageBitmap) {
        var p = null;
        try { p = createImageBitmap(file, { imageOrientation: 'from-image' }); } catch (e) { p = null; }
        if (p && p.then) {
          p.then(
            function (bm) { useSource(bm, bm.width, bm.height, how); },
            function () { viaImg(file, how); }
          );
          return;
        }
      }
      viaImg(file, how);
    }

    function viaImg(file, how) {
      var url;
      try {
        url = URL.createObjectURL(file);
      } catch (e) {
        showErr('この画像を開けませんでした。スクリーンショットか JPEG / PNG でお試しください');
        return;
      }
      var im = new Image();
      im.onload = function () {
        useSource(im, im.naturalWidth, im.naturalHeight, how);
        try { URL.revokeObjectURL(url); } catch (e) {}
      };
      im.onerror = function () {
        try { URL.revokeObjectURL(url); } catch (e) {}
        showErr('この形式は開けません。スクリーンショットか JPEG / PNG でお試しください');
      };
      im.src = url;
    }

    /* 長辺 1600px まで縮小した表示用canvasを作る。EXIFの回転は
       createImageBitmap の imageOrientation:'from-image' が扱う。 */
    function useSource(source, w, h, how) {
      showErr('');
      if (!w || !h) {
        showErr('この画像を開けませんでした。スクリーンショットか JPEG / PNG でお試しください');
        return;
      }
      clearRef(true);
      /* 別の参考画像を置いたら、自動の並べ替えは再開する
         (「元の並びに戻す」はその参考画像のあいだだけ効く) */
      sortEnabled = true;
      var k = Math.min(1, MAX_SIDE / Math.max(w, h));
      var cw = Math.max(1, Math.round(w * k));
      var ch = Math.max(1, Math.round(h * k));
      var c = document.createElement('canvas');
      c.width = cw;
      c.height = ch;
      var ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(source, 0, 0, cw, ch);
      ref.src = c;
      ref.sctx = ctx;
      /* 元のデータはここで捨てる。以降は表示用canvasだけを使う */
      if (source && typeof source.close === 'function') {
        try { source.close(); } catch (e) {}
      }
      root.classList.add('has-ref');
      if (tipEl) tipEl.hidden = false;
      if (isMobile()) {
        /* スマホ: 貼りつく小窓は出さず、そのまま全画面の「色を拾う」シートを開く。
           1画面に1つのこと。色を拾うあいだは画像だけに集中してもらう。 */
        if (panel) panel.hidden = true;
        renderChip();
        renderSuggest();
        renderRefRow();
        openPicker();
      } else {
        if (panel) panel.hidden = false;
        setView(stageEl, canvasEl);
        fitView();
        measure();
        drawRef();
        renderChip();
        renderSuggest();
      }
      say('参考画像を表示しました。髪の部分をタップすると、近い色を5つお出しします');
      /* 計測。画像の内容・ファイル名は送らない。方式だけ */
      track('wc2_color_image_added', { method: how });
    }

    function stageSize() {
      if (!view.stage) return { w: 1, h: 1 };
      var r = view.stage.getBoundingClientRect();
      return { w: Math.max(1, r.width), h: Math.max(1, r.height) };
    }

    function fitView() {
      if (!ref.src) return;
      var s = stageSize();
      ref.base = Math.min(s.w / ref.src.width, s.h / ref.src.height);
      ref.scale = ref.base;
      ref.tx = (s.w - ref.src.width * ref.scale) / 2;
      ref.ty = (s.h - ref.src.height * ref.scale) / 2;
      ref.cur = null;
    }

    /* 画面の大きさが変わったら、はみ出し方が変わるので位置を収め直す。
       拡大していないときは、新しい大きさに合わせて入れ直す。 */
    function fitClampOnResize() {
      if (!ref.src) return;
      if (ref.scale <= ref.base * 1.02) fitView();
      else clampView();
    }

    function clampView() {
      var s = stageSize();
      var w = ref.src.width * ref.scale;
      var h = ref.src.height * ref.scale;
      ref.tx = Math.max(Math.min(0, s.w - w), Math.min(Math.max(0, s.w - w), ref.tx));
      ref.ty = Math.max(Math.min(0, s.h - h), Math.min(Math.max(0, s.h - h), ref.ty));
    }

    function crosshair(ctx, x, y) {
      ctx.beginPath();
      ctx.moveTo(x - 14, y); ctx.lineTo(x - 4, y);
      ctx.moveTo(x + 4, y); ctx.lineTo(x + 14, y);
      ctx.moveTo(x, y - 14); ctx.lineTo(x, y - 4);
      ctx.moveTo(x, y + 4); ctx.lineTo(x, y + 14);
      ctx.stroke();
    }

    function drawRef() {
      if (!ref.src || !view.canvas || !view.stage) return;
      var sr = view.stage.getBoundingClientRect();
      if (sr.width < 1 || sr.height < 1) return;
      var s = stageSize();
      var dpr = Math.min(2, window.devicePixelRatio || 1);
      var canvasEl = view.canvas;
      canvasEl.width = Math.round(s.w * dpr);
      canvasEl.height = Math.round(s.h * dpr);
      var ctx = canvasEl.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#f2f2f2';
      ctx.fillRect(0, 0, s.w, s.h);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(
        ref.src, 0, 0, ref.src.width, ref.src.height,
        ref.tx, ref.ty, ref.src.width * ref.scale, ref.src.height * ref.scale
      );
      /* 拾った点は1つだけなので、番号は付けずに丸印だけを出す */
      if (ref.pt) {
        var px = ref.tx + ref.pt.x * ref.scale;
        var py = ref.ty + ref.pt.y * ref.scale;
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#ffffff';
        ctx.beginPath(); ctx.arc(px, py, 10, 0, 2 * PI); ctx.stroke();
        ctx.strokeStyle = '#1a1a1a';
        ctx.beginPath(); ctx.arc(px, py, 8, 0, 2 * PI); ctx.stroke();
      }
      if (ref.kb && ref.cur) {
        var cx = ref.tx + ref.cur.x * ref.scale;
        var cy = ref.ty + ref.cur.y * ref.scale;
        ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 3; crosshair(ctx, cx, cy);
        ctx.strokeStyle = '#1a1a1a'; ctx.lineWidth = 1; crosshair(ctx, cx, cy);
      }
    }

    function toSrc(vx, vy) {
      return { x: (vx - ref.tx) / ref.scale, y: (vy - ref.ty) / ref.scale };
    }

    /* 周囲5×5px の平均を取る */
    function sampleAt(sx, sy) {
      var r = 2;
      var w = r * 2 + 1;
      var h = r * 2 + 1;
      var x = Math.round(sx) - r;
      var y = Math.round(sy) - r;
      x = Math.max(0, Math.min(ref.src.width - w, x));
      y = Math.max(0, Math.min(ref.src.height - h, y));
      if (ref.src.width < w || ref.src.height < h) {
        x = 0; y = 0; w = ref.src.width; h = ref.src.height;
      }
      var d;
      try { d = ref.sctx.getImageData(x, y, w, h).data; } catch (e) { return null; }
      var R = 0, G = 0, B = 0, n = 0;
      for (var i = 0; i < d.length; i += 4) { R += d[i]; G += d[i + 1]; B += d[i + 2]; n++; }
      if (!n) return null;
      return [Math.round(R / n), Math.round(G / n), Math.round(B / n)];
    }

    /* 拾う点は1つだけ。別の場所をタップしたら、その点が新しい点に置き換わる。
       置き換えるたびに、提案(上位5色に戻す)・グリッドの印・系統の並びを計算し直す。 */
    function pickAt(sx, sy) {
      if (!ref.src) return;
      if (sx < 0 || sy < 0 || sx >= ref.src.width || sy >= ref.src.height) return;
      var rgb = sampleAt(sx, sy);
      if (!rgb) { toast('この点の色を読めませんでした'); return; }
      var had = !!ref.pt;
      ref.pt = { x: sx, y: sy, rgb: rgb, lab: toLab(rgb) };
      if (tipEl) tipEl.hidden = true;
      resetSgShown();
      drawRef();
      renderChip();
      syncPickerUI();
      renderRefRow();
      ensureLab();
      tryRuntimeReps(renderSuggest);
      renderSuggest();
      say(had
        ? '拾う色を変えました。画面上で近い色を5つ表示しています'
        : '色を拾いました。画面上で近い色を5つ表示しています');
      /* 計測。拾った色の値も、何点目かも送らない(点は常に1つなので) */
      track('wc2_color_picked', {});
    }

    function clearPick() {
      ref.pt = null;
      if (tipEl) tipEl.hidden = false;
      resetSgShown();
      drawRef();
      renderChip();
      syncPickerUI();
      renderRefRow();
      renderSuggest();
      say('拾った色を消しました');
    }

    function renderChip() {
      if (!chipsEl) return;
      chipsEl.innerHTML = '';
      if (!ref.pt) return;
      var chip = document.createElement('span');
      chip.className = 'wc2c-chip';
      var dot = document.createElement('span');
      dot.className = 'wc2c-chip__dot';
      dot.style.background = rgbToCss(ref.pt.rgb);
      var label = document.createElement('span');
      label.textContent = '拾った色';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = '×';
      btn.setAttribute('aria-label', '拾った色を消す');
      btn.addEventListener('click', clearPick);
      chip.appendChild(dot);
      chip.appendChild(label);
      chip.appendChild(btn);
      chipsEl.appendChild(chip);
    }

    /* --------------------------------------------------- 画面上で近い色 */

    var suggestEl = $('[data-wc2c-suggest]');
    var sgListEl = $('[data-wc2c-sglist]');
    var sgMoreEl = $('[data-wc2c-sgmore]');
    var suggestOpened = false;
    /* いま提案に出ている色(表示順)。拡大の前後送りの並びに使う */
    var sgItems = [];

    /* 出す件数。既定は上位5色で、「他の候補を表示」を押すたびに5ずつ増えて15で止まる。
       拾う色を置き換えた・消した・画像を消したときは上位5色に戻す。 */
    var SG_STEP = 5;
    var SG_MAX = 15;
    var sgShown = SG_STEP;
    function resetSgShown() { sgShown = SG_STEP; }

    /* 近い順に全色を並べた配列を返す(切り出しは呼び出し側で行う) */
    function nearest(lab) {
      return items
        .filter(function (it) { return !!it.lab; })
        .map(function (it) { return { it: it, d: deltaE00(lab, it.lab) }; })
        .sort(function (a, b) { return a.d - b.d || a.it.i - b.it.i; });
    }

    function renderSuggest() {
      if (!suggestEl) return;
      if (!ref.src || !ref.pt) {
        suggestEl.hidden = true;
        sgItems = [];
        markNear([]);
        syncFamOrder();
        return;
      }
      suggestEl.hidden = false;
      if (!suggestOpened) {
        suggestOpened = true;
        track('wc2_color_suggestion_opened', {});
      }
      var ranked = nearest(ref.pt.lab);
      var cap = Math.min(SG_MAX, ranked.length);
      if (sgShown > cap) sgShown = cap;
      var list = ranked.slice(0, Math.max(0, sgShown));
      sgItems = list.map(function (r) { return r.it; });
      sgListEl.innerHTML = '';
      list.forEach(function (r) { sgListEl.appendChild(makeSgCard(r)); });
      /* 6件以上出すときは、スマホでも横スクロールをやめて3列で折り返す
         (15件を横に流すと、後ろのほうが見つからない) */
      sgListEl.classList.toggle('is-more', list.length > SG_STEP);
      renderSgMore(cap);
      markNear(list.map(function (r) { return r.it.n; }));
      /* 系統の並びも、いま拾っている色で計算し直す */
      syncFamOrder();
    }

    /* 「他の候補を表示」/「上位5色だけに戻す」。上限は15位まで。 */
    function renderSgMore(cap) {
      if (!sgMoreEl) return;
      sgMoreEl.innerHTML = '';
      if (cap <= SG_STEP) return;
      if (sgShown < cap) {
        sgMoreEl.appendChild(
          makeSgMoreBtn('data-wc2c-sg-more', '他の候補を表示', function () {
            sgShown = Math.min(cap, sgShown + SG_STEP);
            /* 計測。何段目まで開いたかだけ(画像の内容・色の値は送らない) */
            track('wc2_color_more_suggestions', { step: Math.round(sgShown / SG_STEP) });
            renderSuggest();
            say('候補を' + SG_STEP + 'つ追加しました');
            focusSgMore();
            alignSgList();
          })
        );
      }
      if (sgShown > SG_STEP) {
        sgMoreEl.appendChild(
          makeSgMoreBtn('data-wc2c-sg-less', '上位5色だけに戻す', function () {
            resetSgShown();
            renderSuggest();
            say('上位5色だけの表示に戻しました');
            focusSgMore();
            alignSgList();
          })
        );
      }
    }

    /* 件数を変えたあと、**提案の1行目の色見本の上端**を、貼りついているもの
       (固定ヘッダー + 系統タブ帯 +(スマホ・タブレットでは)参考画像の小窓)の
       下端 +24px にそろえる。タブでの移動と同じ規則。

       これを入れる前は、押したボタンへフォーカスを戻すところでブラウザが
       ボタンを画面内に入れようとして下へスクロールし、最初の5色がタブ帯の下に
       潜って見えなくなっていた(フォーカスは preventScroll に変えてある)。 */
    function stickyBottomForList() {
      var base = (lastH < 0 ? 0 : lastH) + (lastT < 0 ? 0 : lastT);
      /* 参考画像パネルは、PCでは左の列なので一覧の上には重ならない。
         横に重なっているときだけ、その高さも見込む(貼りついたあとの位置で数える)。 */
      if (panel && !panel.hidden && sgListEl) {
        var pr = panel.getBoundingClientRect();
        var lr = sgListEl.getBoundingClientRect();
        if (pr.height > 0 && pr.right > lr.left + 1 && pr.left < lr.right - 1) base += pr.height;
      }
      return base;
    }

    function sgListTargetTop() {
      return Math.max(0, sgListEl.getBoundingClientRect().top + window.pageYOffset
        - (stickyBottomForList() + 24));
    }

    /* 提案の先頭の着地先。出ていないときは null(動かさない) */
    function sgListTarget() {
      if (!sgListEl || !suggestEl || suggestEl.hidden) return null;
      return sgListTargetTop();
    }

    /* 「他の候補を表示」など、ページ内で件数を変えたときの位置合わせ。
       すでにそろっていれば動かさない。着地の確認と合わせ直しは landAt() が行う。 */
    function alignSgList() {
      if (sgListTarget() === null) return;
      afterLayout(function () {
        measure();
        landAt(sgListTarget, false);
      });
    }

    function makeSgMoreBtn(attr, label, fn) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'wc2c-linklike wc2c-sgmore__btn';
      b.setAttribute(attr, '');
      b.textContent = label;
      b.addEventListener('click', fn);
      return b;
    }

    /* 押したボタンは作り直されるので、同じ位置のボタンへフォーカスを戻す。
       preventScroll で、フォーカスによる自動スクロールを起こさない
       (位置合わせは alignSgList() が行う)。 */
    function focusSgMore() {
      var b = $('[data-wc2c-sg-more]') || $('[data-wc2c-sg-less]');
      if (!b || !b.focus) return;
      try { b.focus({ preventScroll: true }); } catch (e) { b.focus(); }
    }

    function makeSgCard(r) {
      var it = r.it;
      var li = document.createElement('li');
      li.className = 'wc2c-sg';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wc2c-sg__btn';
      btn.setAttribute('aria-label', it.n + ' を大きく見る');
      var wrap = document.createElement('span');
      wrap.className = 'wc2c-sg__img';
      if (it.img) {
        var clone = it.img.cloneNode(true);
        clone.removeAttribute('loading');
        wrap.appendChild(clone);
      }
      btn.appendChild(wrap);
      /* 提案カードから開いたときは、提案の順(1〜15位)で前後に送る */
      btn.addEventListener('click', function () { openSheet(it, sgItems.slice()); });

      var name = document.createElement('span');
      name.className = 'wc2c-sg__name';
      name.textContent = it.n;

      var near = document.createElement('span');
      near.className = 'wc2c-sg__near';
      near.textContent = nearWord(r.d);

      var add = document.createElement('button');
      add.type = 'button';
      add.className = 'wc2c-sg__add';
      var on = isPicked(it.n);
      add.setAttribute('aria-pressed', on ? 'true' : 'false');
      add.setAttribute('data-wc2c-add', it.n);
      add.textContent = on ? '候補に入れた' : '候補に入れる';
      add.addEventListener('click', function () { togglePick(it.n); });

      li.appendChild(btn);
      li.appendChild(name);
      li.appendChild(near);
      li.appendChild(add);
      return li;
    }

    function markNear(names) {
      var set = {};
      names.forEach(function (n) { set[n] = 1; });
      items.forEach(function (it) { it.el.classList.toggle('is-near', !!set[it.n]); });
    }

    /* ----------------------------- 参考画像の操作(タップ・ドラッグ・拡大) */

    /* 参考画像を出す面(PCのパネル / スマホの「色を拾う」シート)に、
       タップ(色を拾う)・ドラッグ(移動)・ピンチ(拡大)・ダブルタップ(拡大/戻す)を付ける。
       いま生きている面(view.canvas)でだけ効かせる。

       拡大・移動とタップを取り違えないよう、**動いた量が小さいときだけ**タップ扱いにする。
       2本指が触れているあいだは色を拾わない。 */
    function bindPickSurface(canvasEl, stageEl) {
      if (!canvasEl || !stageEl) return;
      var pointers = {};
      var nPtr = 0;
      var moved = false;
      var startX = 0;
      var startY = 0;
      var pinch = null;
      var multi = false;   /* この操作のあいだに2本目が触れたか */
      var lastTap = 0;
      var lastTapX = 0;
      var lastTapY = 0;
      var mine = function () { return view.canvas === canvasEl; };

      canvasEl.addEventListener('pointerdown', function (e) {
        if (!ref.src || !mine()) return;
        try { canvasEl.setPointerCapture(e.pointerId); } catch (err) {}
        pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        nPtr++;
        if (nPtr === 1) { moved = false; multi = false; startX = e.clientX; startY = e.clientY; }
        if (nPtr === 2) { pinch = pinchState(); multi = true; }
      });

      canvasEl.addEventListener('pointermove', function (e) {
        if (!ref.src || !mine() || !pointers[e.pointerId]) return;
        var prev = pointers[e.pointerId];
        pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        if (nPtr === 1) {
          if (Math.abs(e.clientX - startX) > 6 || Math.abs(e.clientY - startY) > 6) moved = true;
          ref.tx += e.clientX - prev.x;
          ref.ty += e.clientY - prev.y;
          clampView();
          drawRef();
        } else if (nPtr >= 2 && pinch) {
          moved = true;
          var now = pinchState();
          if (now && pinch.dist > 0) { zoomAt(now.cx, now.cy, now.dist / pinch.dist); pinch = now; }
        }
      });

      var endPtr = function (e) {
        if (!pointers[e.pointerId]) return;
        delete pointers[e.pointerId];
        nPtr = Math.max(0, nPtr - 1);
        if (nPtr < 2) pinch = null;
        if (nPtr === 0 && !moved && !multi && ref.src && mine()) {
          var now = Date.now();
          var dbl = now - lastTap < 320 &&
            Math.abs(e.clientX - lastTapX) < 24 && Math.abs(e.clientY - lastTapY) < 24;
          lastTap = now;
          lastTapX = e.clientX;
          lastTapY = e.clientY;
          var r = stageEl.getBoundingClientRect();
          var vx = e.clientX - r.left;
          var vy = e.clientY - r.top;
          if (dbl) {
            /* ダブルタップは拡大 / 戻す。色は拾い直さない(1タップ目の色はそのまま) */
            lastTap = 0;
            if (ref.scale > ref.base * 1.4) { fitView(); drawRef(); }
            else zoomAt(vx, vy, 2.5);
            return;
          }
          var p = toSrc(vx, vy);
          ref.cur = { x: p.x, y: p.y };
          pickAt(p.x, p.y);
        }
      };
      canvasEl.addEventListener('pointerup', endPtr);
      canvasEl.addEventListener('pointercancel', endPtr);

      function pinchState() {
        var ks = Object.keys(pointers);
        if (ks.length < 2) return null;
        var a = pointers[ks[0]];
        var b = pointers[ks[1]];
        var r = stageEl.getBoundingClientRect();
        return {
          cx: (a.x + b.x) / 2 - r.left,
          cy: (a.y + b.y) / 2 - r.top,
          dist: Math.hypot(a.x - b.x, a.y - b.y)
        };
      }

      function zoomAt(vx, vy, k) {
        var s0 = ref.scale;
        var s1 = Math.max(ref.base * 0.9, Math.min(ref.base * 12, s0 * k));
        if (s1 === s0) return;
        ref.tx = vx - (vx - ref.tx) * (s1 / s0);
        ref.ty = vy - (vy - ref.ty) * (s1 / s0);
        ref.scale = s1;
        clampView();
        drawRef();
      }

      canvasEl.addEventListener('wheel', function (e) {
        if (!ref.src || !mine()) return;
        e.preventDefault();
        var r = stageEl.getBoundingClientRect();
        zoomAt(e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY * 0.0016));
      }, { passive: false });

      canvasEl.addEventListener('focus', function () {
        if (!mine()) return;
        ref.kb = true;
        if (!ref.cur && ref.src) ref.cur = { x: ref.src.width / 2, y: ref.src.height / 2 };
        drawRef();
      });
      canvasEl.addEventListener('blur', function () { ref.kb = false; drawRef(); });

      canvasEl.addEventListener('keydown', function (e) {
        if (!ref.src || !mine()) return;
        var step = e.shiftKey ? 1 : Math.max(1, Math.round(12 / ref.scale));
        var k = e.key;
        if (k === 'ArrowLeft' || k === 'ArrowRight' || k === 'ArrowUp' || k === 'ArrowDown') {
          e.preventDefault();
          if (!ref.cur) ref.cur = { x: ref.src.width / 2, y: ref.src.height / 2 };
          if (k === 'ArrowLeft') ref.cur.x -= step;
          if (k === 'ArrowRight') ref.cur.x += step;
          if (k === 'ArrowUp') ref.cur.y -= step;
          if (k === 'ArrowDown') ref.cur.y += step;
          ref.cur.x = Math.max(0, Math.min(ref.src.width - 1, ref.cur.x));
          ref.cur.y = Math.max(0, Math.min(ref.src.height - 1, ref.cur.y));
          ref.kb = true;
          keepCursorVisible();
          drawRef();
        } else if (k === 'Enter' || k === ' ') {
          e.preventDefault();
          if (ref.cur) pickAt(ref.cur.x, ref.cur.y);
        } else if (k === '+' || k === '=') {
          e.preventDefault();
          var s = stageSize();
          zoomAt(s.w / 2, s.h / 2, 1.25);
        } else if (k === '-' || k === '_') {
          e.preventDefault();
          var s2 = stageSize();
          zoomAt(s2.w / 2, s2.h / 2, 0.8);
        }
      });

      function keepCursorVisible() {
        var s = stageSize();
        var x = ref.tx + ref.cur.x * ref.scale;
        var y = ref.ty + ref.cur.y * ref.scale;
        var m = 24;
        if (x < m) ref.tx += m - x; else if (x > s.w - m) ref.tx -= x - (s.w - m);
        if (y < m) ref.ty += m - y; else if (y > s.h - m) ref.ty -= y - (s.h - m);
        clampView();
      }
    }

    bindPickSurface(canvasEl, stageEl);
    bindPickSurface(pickCanvasEl, pickStageEl);

    /* ===================================== 「色を拾う」シート(スマホ)

       画像を選んだら、そのまま全画面のシートが開く。画像は幅いっぱい・縦は残りに収める。
       タップすると丸印が付き、下に「拾った色」のチップと主ボタンが出る。
       決める前は何度でもタップし直せる(置き換え)。
       決めずに閉じたときは、開く前の状態に戻す。 */

    var pickOv = $('[data-wc2c-pick-ov]');
    var pickSheetEl = $('[data-wc2c-pick-sheet]');
    var pickTipEl = $('[data-wc2c-picktip]');
    var pickChipEl = $('[data-wc2c-pickchip]');
    var pickChipDotEl = $('[data-wc2c-pickchip-dot]');
    var pickGoEl = $('[data-wc2c-pick-confirm]');
    var pickBefore = null;
    var pickDecided = false;
    var pickCfg = null;

    function pickerOpen() { return !!(pickOv && pickOv.classList.contains('is-open')); }

    function syncPickerUI() {
      if (!pickOv) return;
      var has = !!ref.pt;
      if (pickTipEl) pickTipEl.hidden = has;
      if (pickChipEl) pickChipEl.hidden = !has;
      if (pickChipDotEl && has) pickChipDotEl.style.background = rgbToCss(ref.pt.rgb);
      if (pickGoEl) pickGoEl.disabled = !has;
    }

    function openPicker() {
      if (!pickOv || !ref.src || !isMobile()) return;
      pickBefore = ref.pt
        ? { x: ref.pt.x, y: ref.pt.y, rgb: ref.pt.rgb.slice(), lab: ref.pt.lab.slice() }
        : null;
      pickDecided = false;
      pickCfg = modalOpen({
        ov: pickOv,
        box: pickSheetEl,
        full: true,
        focus: $('[data-wc2c-pick-close]'),
        onClose: onPickerClosed
      });
      setView(pickStageEl, pickCanvasEl);
      afterLayout(function () {
        if (!pickerOpen()) return;
        fitView();
        drawRef();
        syncPickerUI();
      });
      syncPickerUI();
      track('wc2_color_picker_opened', {});
    }

    function onPickerClosed(cfg) {
      setView(null, null);
      if (!pickDecided) {
        /* 色を決めずに閉じた。開く前の状態に戻す */
        ref.pt = pickBefore;
        resetSgShown();
        renderChip();
        renderSuggest();
      } else if (cfg) {
        /* 決めたときは、提案の見出しへフォーカスを移す */
        var h = $('#wc2c-sg-title');
        if (h) cfg.restore = h;
      }
      renderRefRow();
    }

    bindClick('[data-wc2c-pick-close]', function () { modalClose(); });
    bindClick('[data-wc2c-pick-reset]', function () { fitView(); drawRef(); });
    bindClick('[data-wc2c-pick-confirm]', function () {
      if (!ref.pt) return;
      pickDecided = true;
      track('wc2_color_picker_confirmed', {});
      /* 閉じたあとの着地先を**先に**決めておく。実際に動かすのは、シートを隠し・
         履歴の後始末をし・背面の固定を解除し・レイアウトが決まったあと(finishClose)。
         ここで動かすと、ブラウザのスクロール復元や固定解除と競合して打ち消される。 */
      landFn = sgListTarget;
      modalClose();
    });
    bindSwipeClose(pickSheetEl ? pickSheetEl.querySelector('.wc2c-full__bar') : null);

    /* --------------------- 拾ったあとの参考画像(貼りつかない1行・スマホ) */

    var refRowEl = $('[data-wc2c-refrow]');
    var refThumbEl = $('[data-wc2c-refthumb]');
    var refRowDotEl = $('[data-wc2c-refrow-dot]');

    function renderRefRow() {
      if (!refRowEl) return;
      var on = !!(ref.src && isMobile());
      refRowEl.hidden = !on;
      if (!on) return;
      if (refRowDotEl) {
        refRowDotEl.hidden = !ref.pt;
        if (ref.pt) refRowDotEl.style.background = rgbToCss(ref.pt.rgb);
      }
      drawRefThumb();
    }

    function drawRefThumb() {
      if (!refThumbEl || !ref.src || !refRowEl || refRowEl.hidden) return;
      var side = 48;
      var dpr = Math.min(2, window.devicePixelRatio || 1);
      refThumbEl.width = Math.round(side * dpr);
      refThumbEl.height = Math.round(side * dpr);
      var ctx = refThumbEl.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#f2f2f2';
      ctx.fillRect(0, 0, side, side);
      var k = Math.max(side / ref.src.width, side / ref.src.height);
      var w = ref.src.width * k;
      var h = ref.src.height * k;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(ref.src, (side - w) / 2, (side - h) / 2, w, h);
    }

    bindClick('[data-wc2c-repick]', function () { openPicker(); });
    bindClick('[data-wc2c-ref-clear2]', function () { clearRef(false); });

    /* ------------------------------------------------- パネルのボタン */

    bindClick('[data-wc2c-ref-reset]', function () { fitView(); drawRef(); });
    bindClick('[data-wc2c-ref-min]', function () {
      var on = panel.classList.toggle('is-min');
      this.textContent = on ? '元の大きさに' : '最小化';
      this.setAttribute('aria-pressed', on ? 'true' : 'false');
      measure();
      if (!on) drawRef();
    });
    bindClick('[data-wc2c-ref-clear]', function () {
      clearRef(false);
      var b = $('[data-wc2c-pick-file]');
      if (b) b.focus();
    });

    function clearRef(keepOpen) {
      ref.pt = null;
      resetSgShown();
      ref.cur = null;
      ref.kb = false;
      ref.src = null;
      ref.sctx = null;
      if (!keepOpen) {
        root.classList.remove('has-ref');
        if (panel) {
          panel.hidden = true;
          panel.classList.remove('is-min');
        }
        var mb = $('[data-wc2c-ref-min]');
        if (mb) { mb.textContent = '最小化'; mb.setAttribute('aria-pressed', 'false'); }
        if (tipEl) tipEl.hidden = false;
        if (fileEl) fileEl.value = '';
        suggestOpened = false;
        setView(isMobile() ? null : stageEl, isMobile() ? null : canvasEl);
        renderChip();
        syncPickerUI();
        renderRefRow();
        renderSuggest();
        measure();
        say('参考画像を消しました');
      }
    }

    /* 画面の幅がスマホ / PC をまたいだとき、参考画像の見せ方を切り替える。
       スマホ専用の全画面シートが開いていたら閉じる。 */
    function onModeChange() {
      placeSortNote();
      if (ref.src) {
        if (isMobile()) {
          if (panel) panel.hidden = true;
          if (!pickerOpen()) setView(null, null);
        } else {
          if (panel) panel.hidden = false;
          setView(stageEl, canvasEl);
          fitView();
        }
      }
      var top = modalStack[modalStack.length - 1];
      if (!isMobile() && top && top.full) modalClose();
      renderRefRow();
      renderPicks();
      measure();
      drawRef();
      syncTray();
    }
    if (mqMobile.addEventListener) mqMobile.addEventListener('change', onModeChange);
    else if (mqMobile.addListener) mqMobile.addListener(onModeChange);
    window.addEventListener('pagehide', function () { clearRef(false); });

    /* 高さのつまみ(スマホ) */
    var gripEl = $('[data-wc2c-refgrip]');
    if (gripEl && panel && stageEl) {
      var gripOn = false;
      var gripY = 0;
      var gripH = 0;
      gripEl.addEventListener('pointerdown', function (e) {
        gripOn = true;
        try { gripEl.setPointerCapture(e.pointerId); } catch (err) {}
        gripY = e.clientY;
        gripH = stageEl.getBoundingClientRect().height;
      });
      gripEl.addEventListener('pointermove', function (e) {
        if (!gripOn) return;
        var h = Math.max(120, Math.min(window.innerHeight * 0.6, gripH + (e.clientY - gripY)));
        panel.style.setProperty('--wc2c-ref-h', Math.round(h) + 'px');
        drawRef();
        measure();
      });
      var gripUp = function () { gripOn = false; };
      gripEl.addEventListener('pointerup', gripUp);
      gripEl.addEventListener('pointercancel', gripUp);
    }

    /* ----------------------------------------- 入口ブロックの開閉(コンパクト)

       1画面目に色見本が入るよう、入口ブロックは**低い帯**で出す。
       「使い方を見る」で、説明・3ステップ・図解・ドロップ領域の破線を開く。
       開いた状態は sessionStorage に覚える(失敗してもページは動く)。 */

    var ENTRY_KEY = 'wigcut_color_entry_open_v1';
    var entryMoreEl = $('[data-wc2c-entry-more]');
    var entryToggleEl = $('[data-wc2c-entry-toggle]');

    function setEntryOpen(on, remember) {
      if (!entryMoreEl || !entryToggleEl) return;
      entryMoreEl.hidden = !on;
      entryToggleEl.setAttribute('aria-expanded', on ? 'true' : 'false');
      var label = entryToggleEl.getAttribute(on ? 'data-wc2c-label-close' : 'data-wc2c-label-open');
      if (label) entryToggleEl.textContent = label;
      if (remember) {
        try { sessionStorage.setItem(ENTRY_KEY, on ? '1' : '0'); } catch (e) {}
      }
      measure();
      if (on && ref.src) drawRef();
    }

    if (entryToggleEl && entryMoreEl) {
      var entryWas = null;
      try { entryWas = sessionStorage.getItem(ENTRY_KEY); } catch (e) { entryWas = null; }
      setEntryOpen(entryWas === '1', false);
      entryToggleEl.addEventListener('click', function () {
        setEntryOpen(entryMoreEl.hidden, true);
      });
    }

    /* ------------------------ 画像の入口(ファイル・ドラッグ・貼り付け) */

    bindClick('[data-wc2c-pick-file]', function () { if (fileEl) fileEl.click(); });
    if (fileEl) {
      fileEl.addEventListener('change', function (e) {
        var f = e.target.files && e.target.files[0];
        if (f) fromFile(f, 'file');
      });
    }
    if (dropEl) {
      ['dragenter', 'dragover'].forEach(function (t) {
        dropEl.addEventListener(t, function (e) {
          e.preventDefault();
          dropEl.classList.add('is-over');
          /* 画像をドラッグしてきたら、コンパクト表示でも自動で開いて
             破線のドロップ領域を見せる(覚えさせない) */
          if (t === 'dragenter' && entryMoreEl && entryMoreEl.hidden) setEntryOpen(true, false);
        });
      });
      ['dragleave', 'drop'].forEach(function (t) {
        dropEl.addEventListener(t, function () { dropEl.classList.remove('is-over'); });
      });
      dropEl.addEventListener('drop', function (e) {
        e.preventDefault();
        var dt = e.dataTransfer;
        var f = dt && dt.files && dt.files[0];
        if (f) fromFile(f, 'drop');
      });
    }
    document.addEventListener('paste', function (e) {
      var cd = e.clipboardData;
      if (!cd) return;
      var list = cd.items || [];
      for (var i = 0; i < list.length; i++) {
        if (list[i].kind === 'file') {
          var f = list[i].getAsFile();
          if (f) { fromFile(f, 'paste'); return; }
        }
      }
    });

    /* テスト用の図形。テーマエディタのデザインモードのときだけボタンが出る
       (Liquid 側で出し分けている)。版権画像を同梱しないための動作確認用。 */
    bindClick('[data-wc2c-demo]', function () {
      var w = 900, h = 1200;
      var c = document.createElement('canvas');
      c.width = w; c.height = h;
      var x = c.getContext('2d');
      x.fillStyle = '#efefef'; x.fillRect(0, 0, w, h);
      x.save();
      x.beginPath(); x.ellipse(450, 620, 300, 420, 0, 0, 2 * PI); x.clip();
      x.fillStyle = '#8a5a33'; x.fillRect(0, 0, w, h);
      x.fillStyle = '#4a3020'; x.fillRect(0, 0, 330, h);
      x.fillStyle = '#c79a63'; x.fillRect(560, 0, 100, h);
      var g = x.createLinearGradient(0, 180, 0, 1060);
      g.addColorStop(0, 'rgba(255,255,255,.16)');
      g.addColorStop(1, 'rgba(0,0,0,.16)');
      x.fillStyle = g; x.fillRect(700, 0, 200, h);
      x.restore();
      useSource(c, w, h, 'demo');
    });

    /* ------------------------------------------------------------ 候補 */

    var picks = load();
    var trayEl = $('[data-wc2c-tray]');
    var trayInnerEl = $('.wc2c-tray__inner');
    var trayLabelEl = $('[data-wc2c-tray-label]');
    var trayItemsEl = $('[data-wc2c-tray-items]');
    var trayClearEl = $('[data-wc2c-clear-all]');
    var compareBtn = $('[data-wc2c-compare]');

    /* スマホの低いバー。置くのは件数・色の丸・「候補を見る」だけ。
       個別の×・まとめてコピー・すべて消すは、全画面の「候補を見比べる」に置く。 */
    var cbarEl = $('[data-wc2c-cbar]');
    var cbarLabelEl = $('[data-wc2c-cbar-label]');
    var cbarDotsEl = $('[data-wc2c-cbar-dots]');

    /* --------- 追従LINEボタンとの重なり回避 / トレイのぶんの下余白 ---------

       サイト共通の追従LINEボタン(layout/theme.liquid、右下に固定)は触らない。
       このページの中だけで、
         1) 候補トレイの右側に、追従ボタンの実寸ぶんの余白を確保する
            (チップと操作ボタンがボタンの下に隠れないようにする)
         2) トレイの高さぶん、ページ下部に余白を足す
            (ページ最下部のLINEボタン・FAQ・フッターがトレイに隠れないようにする)
       追従ボタンが無い・隠れているときは余白を 0 にする。 */

    var FAB_SEL = '.wigcut-floating-line';

    /* 追従ボタンの位置と大きさ。**visibility:hidden でも寸法を返す**。
       拡大シートの間はこちらで一時的に visibility:hidden にしているので、
       ここで null を返すと余白が 0 になり、シートを閉じた直後にトレイの
       ボタンが追従ボタンの下に潜る(2026-09-20 に本番で出た不具合)。
       場所を占めていないのは display:none のときだけなので、その場合だけ null。 */
    function fabBox() {
      var el = document.querySelector(FAB_SEL);
      if (!el) return null;
      var cs = getComputedStyle(el);
      if (cs.display === 'none') return null;
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return null;
      return r;
    }

    function syncTray() {
      /* スマホは低いバー、PCは従来のトレイ。下端に出ているほうの高さで下余白を決める。
         追従LINEボタンの避け幅は、スマホではボタン自体を出さないので常に 0。 */
      var mob = isMobile();
      var bar = mob ? cbarEl : trayEl;
      if (!bar) return;
      var open = bar.classList.contains('is-open');
      var gap = 0;
      if (!mob && open && trayInnerEl) {
        var f = fabBox();
        if (f) {
          /* padding-right は自分で書き換えるので、左のpaddingを基準の値として使う */
          var pad = parseFloat(getComputedStyle(trayInnerEl).paddingLeft) || 0;
          var contentRight = trayInnerEl.getBoundingClientRect().right - pad;
          gap = Math.max(0, Math.round(contentRight - f.left + 8));
        }
      }
      root.style.setProperty('--wc2c-fab-gap', gap + 'px');
      /* 余白を入れると折り返しが変わるので、高さは反映後に測る。
         ここで読むとレイアウトが確定してから返る(requestAnimationFrame にすると、
         裏に回っているタブでは動かず余白が入らない)。 */
      var h = open ? Math.round(bar.getBoundingClientRect().height) : 0;
      document.body.style.setProperty('--wc2c-tray-h', h + 'px');
      document.body.classList.toggle('wc2c-tray-open', open && h > 0);
    }

    /* 拡大シート・比較ビューが前に出ている間は追従ボタンを隠す(CSS側で visibility)。
       戻したあとは必ず syncTray() を呼ぶ(呼び出し側の closeSheet / closeCompare)。 */
    function hideFab(on) {
      document.body.classList.toggle('wc2c-hide-fab', !!on);
    }

    /* 追従ボタンはテーマ側の実装で、このページより遅れて挿入されることがある。
       大きさが変わったとき(ResizeObserver)と、表示直後の2回(300ms / 1200ms)に
       測り直して、余白が 0 のまま取り残されないようにする。 */
    var fabRO = null;
    function watchFab() {
      if (fabRO || typeof ResizeObserver !== 'function') return;
      var el = document.querySelector(FAB_SEL);
      if (!el) return;
      fabRO = new ResizeObserver(function () { syncTray(); });
      fabRO.observe(el);
    }
    function remeasureFabLater() {
      setTimeout(function () { watchFab(); syncTray(); }, 300);
      setTimeout(function () { watchFab(); syncTray(); }, 1200);
    }

    function load() {
      try {
        var raw = localStorage.getItem(STORE_KEY);
        if (!raw) return [];
        var a = JSON.parse(raw);
        if (!Array.isArray(a)) return [];
        return a
          .map(function (e) { return typeof e === 'string' ? e : e && e.n; })
          .filter(function (n) { return n && byName[n]; })
          .slice(0, MAX_PICKS);
      } catch (e) {
        return [];
      }
    }
    /* 入れるのは色名とhandleだけ。画像は入れない */
    function save() {
      try {
        localStorage.setItem(
          STORE_KEY,
          JSON.stringify(picks.map(function (n) { return { n: n, h: byName[n] ? byName[n].h : '' }; }))
        );
      } catch (e) {}
    }

    function isPicked(n) { return picks.indexOf(n) >= 0; }

    function togglePick(n) {
      var i = picks.indexOf(n);
      if (i >= 0) {
        picks.splice(i, 1);
      } else {
        if (picks.length >= MAX_PICKS) {
          toast('候補は' + MAX_PICKS + 'つまでです。どれかを外してください');
          return false;
        }
        picks.push(n);
        track('wc2_color_candidate_added', { handle: byName[n] ? byName[n].h : '' });
      }
      save();
      renderPicks();
      return true;
    }

    function renderPicks() {
      items.forEach(function (it) {
        var on = isPicked(it.n);
        it.el.classList.toggle('is-picked', on);
        var mark = it.el.querySelector('.wc2c-sw__mark');
        if (mark) mark.textContent = on ? '✓' : '';
      });
      $$('[data-wc2c-add]').forEach(function (b) {
        var on = isPicked(b.getAttribute('data-wc2c-add'));
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.textContent = on ? '候補に入れた' : '候補に入れる';
      });
      if (trayLabelEl) trayLabelEl.textContent = '候補 ' + picks.length + ' / ' + MAX_PICKS;
      if (trayItemsEl) {
        trayItemsEl.innerHTML = '';
        picks.forEach(function (n) {
          var it = byName[n];
          var chip = document.createElement('span');
          chip.className = 'wc2c-tray__chip';
          if (it && it.img) {
            var thumb = it.img.cloneNode(true);
            thumb.removeAttribute('loading');
            thumb.alt = '';
            chip.appendChild(thumb);
          }
          var label = document.createElement('span');
          label.textContent = n;
          chip.appendChild(label);
          var rm = document.createElement('button');
          rm.type = 'button';
          rm.textContent = '×';
          rm.setAttribute('aria-label', n + 'を候補から外す');
          rm.addEventListener('click', function () { togglePick(n); });
          chip.appendChild(rm);
          trayItemsEl.appendChild(chip);
        });
      }
      if (trayEl) trayEl.classList.toggle('is-open', picks.length > 0);
      if (trayClearEl) trayClearEl.hidden = picks.length === 0;
      /* スマホの低いバー。色の丸は最大5つ、重ねずに並べる */
      if (cbarEl) {
        cbarEl.classList.toggle('is-open', picks.length > 0);
        if (cbarLabelEl) cbarLabelEl.textContent = '候補 ' + picks.length + ' / ' + MAX_PICKS;
        if (cbarDotsEl) {
          cbarDotsEl.innerHTML = '';
          picks.forEach(function (n) {
            var it = byName[n];
            var d = document.createElement('i');
            var src = it && it.img ? it.img.currentSrc || it.img.src : '';
            if (src) d.style.backgroundImage = 'url("' + src + '")';
            cbarDotsEl.appendChild(d);
          });
        }
      }
      syncTray();
      if (compareBtn) compareBtn.disabled = picks.length < 2;
      if (cmpOv && cmpOv.classList.contains('is-open')) renderCompare();
      if (picksOpen()) {
        if (!picks.length) modalClose();
        else renderPicksSheet();
      }
      syncSheetPickBtn();
    }

    function picksText() { return picks.join(' / '); }

    bindClick('[data-wc2c-copy-all]', function () {
      if (!picks.length) return;
      copy(picksText(), copiedMsg());
      track('wc2_color_names_copied', { count: picks.length });
    });

    /* すべて消す。確認ダイアログは出さず、代わりに数秒だけ「元に戻す」を出す。
       別の利用者(テスト)の候補が残っていても、ここから空にできる。 */
    bindClick('[data-wc2c-clear-all]', function () {
      if (!picks.length) return;
      var before = picks.slice();
      picks = [];
      save();
      renderPicks();
      say('候補をすべて消しました');
      toast('候補をすべて消しました', {
        label: '元に戻す',
        fn: function () {
          picks = before.slice(0, MAX_PICKS);
          save();
          renderPicks();
          say('候補を元に戻しました');
        }
      });
    });

    /* ==================================== 「候補を見比べる」(スマホの全画面)

       低いバーの「候補を見る」から開く。参考画像があるときは上に置き、
       「参考画像を隠す」で候補の色見本が画面を広く使えるようにする(選択は覚える)。
       最下部(親指の届く位置)に「候補の名前をまとめてコピー」と「すべて消す」。
       「LINEを開く」は 2026-09-20 に廃止(太志が実機で確認。紛らわしく動線として微妙)。 */

    var PICKS_REF_KEY = 'wigcut_color_picksref_v1';
    var picksOv = $('[data-wc2c-picks-ov]');
    var picksSheetEl = $('[data-wc2c-picks-sheet]');
    var picksGridEl = $('[data-wc2c-picks-grid]');
    var picksRefBoxEl = $('[data-wc2c-picks-ref]');
    var picksRefCvEl = $('[data-wc2c-picks-refcv]');
    var picksRefToggleEl = $('[data-wc2c-picks-reftoggle]');
    var picksDoneEl = $('[data-wc2c-picks-done]');
    var picksDoneTimer = null;
    var picksRefShown = true;
    try { picksRefShown = sessionStorage.getItem(PICKS_REF_KEY) !== '0'; } catch (e) {}

    function picksOpen() { return !!(picksOv && picksOv.classList.contains('is-open')); }

    function renderPicksSheet() {
      if (!picksOv) return;
      var hasRef = !!ref.src;
      var show = hasRef && picksRefShown;
      if (picksRefToggleEl) {
        picksRefToggleEl.hidden = !hasRef;
        picksRefToggleEl.textContent = picksRefShown ? '参考画像を隠す' : '参考画像を表示する';
        picksRefToggleEl.setAttribute('aria-pressed', picksRefShown ? 'true' : 'false');
      }
      if (picksRefBoxEl) picksRefBoxEl.hidden = !show;
      if (picksSheetEl) picksSheetEl.classList.toggle('is-noref', !show);
      if (picksGridEl) {
        picksGridEl.innerHTML = '';
        picks.forEach(function (n) {
          var it = byName[n];
          var li = document.createElement('li');
          var ph = document.createElement('span');
          ph.className = 'wc2c-pick-cell__ph';
          if (it && it.b) {
            var im = document.createElement('img');
            im.src = it.b;
            im.alt = n + 'の色見本';
            im.decoding = 'async';
            ph.appendChild(im);
          }
          var nm = document.createElement('span');
          nm.className = 'wc2c-pick-cell__name';
          nm.textContent = n;
          var off = document.createElement('button');
          off.type = 'button';
          off.className = 'wc2c-pick-cell__off';
          off.textContent = '外す';
          off.setAttribute('aria-label', n + 'を候補から外す');
          off.addEventListener('click', function () { togglePick(n); });
          li.appendChild(ph);
          li.appendChild(nm);
          li.appendChild(off);
          picksGridEl.appendChild(li);
        });
      }
      if (show && picksRefCvEl) afterLayout(function () { drawRefInto(picksRefCvEl); });
    }

    function openPicksSheet() {
      if (!picksOv || !picks.length) return;
      modalOpen({
        ov: picksOv,
        box: picksSheetEl,
        full: true,
        focus: $('[data-wc2c-picks-close]')
      });
      if (picksDoneEl) { picksDoneEl.hidden = true; picksDoneEl.textContent = ''; }
      renderPicksSheet();
      track('wc2_color_candidates_opened', {});
    }

    bindClick('[data-wc2c-picks-open]', openPicksSheet);
    bindClick('[data-wc2c-picks-close]', function () { modalClose(); });
    bindClick('[data-wc2c-picks-reftoggle]', function () {
      picksRefShown = !picksRefShown;
      try { sessionStorage.setItem(PICKS_REF_KEY, picksRefShown ? '1' : '0'); } catch (e) {}
      /* 計測は表示 / 非表示の別だけ。画像の内容も色の値も送らない */
      track('wc2_color_ref_toggled', { shown: picksRefShown ? 'on' : 'off' });
      renderPicksSheet();
      say(picksRefShown ? '参考画像を表示しました' : '参考画像を隠しました');
    });
    bindClick('[data-wc2c-picks-copy]', function () {
      if (!picks.length) return;
      copy(picksText(), copiedMsg(), function (ok) {
        if (!picksDoneEl) return;
        picksDoneEl.hidden = false;
        picksDoneEl.textContent = ok ? copiedMsg() + '。' : COPY_NG;
        /* 案内は6秒で引っ込める。出しっぱなしだと下の固定パネルが高いままで、
           候補の色見本が見える範囲が狭くなるため(文言は変えていない)。 */
        if (picksDoneTimer) clearTimeout(picksDoneTimer);
        picksDoneTimer = setTimeout(function () {
          if (!picksDoneEl) return;
          picksDoneEl.hidden = true;
          picksDoneEl.textContent = '';
        }, 6000);
      });
      track('wc2_color_names_copied', { count: picks.length });
    });
    /* 「LINEを開く」は 2026-09-20 に廃止(太志が実機で確認)。
       計測イベント wc2_color_line_opened も送らなくなった。 */
    bindClick('[data-wc2c-picks-clear]', function () {
      if (!picks.length) return;
      var before = picks.slice();
      picks = [];
      save();
      renderPicks();
      say('候補をすべて消しました');
      toast('候補をすべて消しました', {
        label: '元に戻す',
        fn: function () {
          picks = before.slice(0, MAX_PICKS);
          save();
          renderPicks();
          say('候補を元に戻しました');
        }
      });
    });
    bindSwipeClose(picksSheetEl ? picksSheetEl.querySelector('.wc2c-full__bar') : null);

    /* ------------------- 参考画像を別canvasへ写す(拡大・比較で並べるため) */

    function drawRefInto(cv) {
      if (!ref.src || !cv) return;
      var r = cv.getBoundingClientRect();
      var w = Math.max(1, Math.round(r.width));
      var h = Math.max(1, Math.round(r.height));
      var dpr = Math.min(2, window.devicePixelRatio || 1);
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
      var ctx = cv.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#f2f2f2';
      ctx.fillRect(0, 0, w, h);
      ctx.imageSmoothingQuality = 'high';
      if (!view.stage || !view.canvas) {
        /* 参考画像を出している面が無いとき(スマホ。拾うシートを閉じたあと)。
           拾った点を中心に、枠いっぱいに写す。 */
        var k2 = Math.max(w / ref.src.width, h / ref.src.height);
        var hw = w / (2 * k2);
        var hh = h / (2 * k2);
        var cx = ref.pt ? ref.pt.x : ref.src.width / 2;
        var cy = ref.pt ? ref.pt.y : ref.src.height / 2;
        if (hw * 2 < ref.src.width) cx = Math.max(hw, Math.min(ref.src.width - hw, cx));
        else cx = ref.src.width / 2;
        if (hh * 2 < ref.src.height) cy = Math.max(hh, Math.min(ref.src.height - hh, cy));
        else cy = ref.src.height / 2;
        var ox2 = w / 2 - cx * k2;
        var oy2 = h / 2 - cy * k2;
        ctx.drawImage(ref.src, ox2, oy2, ref.src.width * k2, ref.src.height * k2);
        if (ref.pt) {
          var ax = ox2 + ref.pt.x * k2;
          var ay = oy2 + ref.pt.y * k2;
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#ffffff';
          ctx.beginPath(); ctx.arc(ax, ay, 9, 0, 2 * PI); ctx.stroke();
          ctx.strokeStyle = '#1a1a1a';
          ctx.beginPath(); ctx.arc(ax, ay, 7, 0, 2 * PI); ctx.stroke();
        }
        return;
      }
      /* PCは、パネルで見ているのと同じ拡大・位置のまま写す(従来どおり) */
      var s = stageSize();
      var k = Math.max(w / s.w, h / s.h);
      var fx = s.w / 2;
      var fy = s.h / 2;
      if (ref.pt) {
        fx = ref.tx + ref.pt.x * ref.scale;
        fy = ref.ty + ref.pt.y * ref.scale;
        fx = Math.max(w / (2 * k), Math.min(s.w - w / (2 * k), fx));
        fy = Math.max(h / (2 * k), Math.min(s.h - h / (2 * k), fy));
      }
      var ox = w / 2 - fx * k;
      var oy = h / 2 - fy * k;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(
        ref.src, 0, 0, ref.src.width, ref.src.height,
        ox + ref.tx * k, oy + ref.ty * k,
        ref.src.width * ref.scale * k, ref.src.height * ref.scale * k
      );
      if (ref.pt) {
        var mx = ox + (ref.tx + ref.pt.x * ref.scale) * k;
        var my = oy + (ref.ty + ref.pt.y * ref.scale) * k;
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#ffffff';
        ctx.beginPath(); ctx.arc(mx, my, 9, 0, 2 * PI); ctx.stroke();
        ctx.strokeStyle = '#1a1a1a';
        ctx.beginPath(); ctx.arc(mx, my, 7, 0, 2 * PI); ctx.stroke();
      }
    }

    /* ----------------------------------------------------------- 拡大 */

    var sheetOv = $('[data-wc2c-sheet-ov]');
    var sheetEl = $('[data-wc2c-sheet]');
    var sheetRefToggleEl = $('[data-wc2c-sheet-reftoggle]');
    var sheetModal = null;
    var lastFocus = null;
    var sheetSwipedAt = 0; /* 色を送る横スワイプが起きた時刻。直後の click と区別する */

    /* 前後送りの順は「いま画面に並んでいる順」に合わせる(open-questions 33)。
       グリッドから開いたら画面のDOM順(並べ替え中は近い順、元の並びなら元の順)で 170 件、
       提案カードから開いたら提案の順(1〜15位)で 5 / 10 / 15 件。
       通し番号の母数もその並びの件数にする。 */
    var navSeq = [];
    var cur = -1; /* navSeq の中の位置 */

    /* いま画面に並んでいる順の色の一覧。querySelectorAll は文書順に返すので、
       系統の並べ替え・系統の中の並べ替えの結果がそのまま入る。 */
    function gridSeq() {
      return $$('.wc2c-sw')
        .map(function (el) { return items[parseInt(el.getAttribute('data-wc2c-i'), 10)]; })
        .filter(function (it) { return !!it; });
    }

    /* ------------------------- 拡大表示の中の「その色の他の写真」

       色の商品には毛束の写真のほかに、セット後の写真などが登録されていることがある。
       拡大表示でサムネイルを出し、押すと大きい写真が切り替わるようにする。
       サムネイルの画像を読み込むのは**拡大を開いたとき**で、グリッドには
       URLの文字列しか持たせていない(初期表示の通信は増えない)。 */

    var photosEl = $('[data-wc2c-photos]');
    var photoInfoEl = $('.wc2c-sheet__info');
    var photoActsEl = $('[data-wc2c-sheet-acts]');
    var curPhoto = 0;

    /* 1枚目と同じフォルダのぶんは Liquid 側で共通部分を省いてあるので、ここで戻す */
    function photoDir(it) {
      var b = it.b || '';
      var q = b.indexOf('?');
      var path = q < 0 ? b : b.slice(0, q);
      var k = path.lastIndexOf('/');
      return k < 0 ? '' : path.slice(0, k + 1);
    }

    function photoUrl(it, k, w) {
      var s = it.ph[k];
      if (!s) return '';
      if (s.indexOf('//') !== 0 && !/^https?:/i.test(s)) s = photoDir(it) + s;
      return w ? s.replace(/([?&])width=1200(&|$)/, '$1width=' + w + '$2') : s;
    }

    /* PCは説明文の下・ボタンの上(CSの指定)。
       スマホは下端の固定パネルの先頭 = 大きい写真のすぐ下・色名の上。
       本文側に置くとパネルの裏に隠れて、スクロールしないと見えなくなる。 */
    function placePhotos() {
      if (!photosEl || !photoInfoEl) return;
      if (isMobile()) {
        if (photoInfoEl.firstChild !== photosEl) photoInfoEl.insertBefore(photosEl, photoInfoEl.firstChild);
      } else if (photoActsEl && photoActsEl.parentNode && photosEl.nextSibling !== photoActsEl) {
        photoActsEl.parentNode.insertBefore(photosEl, photoActsEl);
      }
    }

    /* 大きい写真に読ませる幅。表示幅の2倍以上になるように端末に選ばせる
       (375pxの画面・DPR2 なら 750w、DPR3 なら 1500w が選ばれる)。 */
    var MEDIA_WIDTHS = [550, 750, 1100, 1500];

    function photoSrcset(it, k) {
      if (!it.ph.length) return '';
      var out = [];
      for (var i = 0; i < MEDIA_WIDTHS.length; i++) {
        var u = photoUrl(it, k, MEDIA_WIDTHS[i]);
        if (u) out.push(u + ' ' + MEDIA_WIDTHS[i] + 'w');
      }
      return out.join(', ');
    }

    /* 大きい写真を作る(2026-09-22)

       横に流す一覧にして、右に次の写真の端を見せる。1枚ずつ止まる(CSSの scroll-snap)。
       切り替えは、横スワイプ / 左右の矢印 / 下のサムネイル の3つ。
       写真は contain のままなので、比率が違っても切り落とさずに全体が見える。
       商品画像に登録されている alt は使わない(色名+連番にする)。 */
    function setSheetMedia(it) {
      var media = $('[data-wc2c-sheet-media]');
      if (!media) return;
      media.innerHTML = '';
      var n = it.ph.length || 1;
      var multi = n > 1;
      media.classList.toggle('is-carousel', multi);
      for (var k = 0; k < n; k++) media.appendChild(buildSlide(it, k, multi));
      syncPhotoNav();
      /* 色を送ったときに前の色の位置が残らないよう、1枚目を中央にして出す。
         behavior:'auto' は、前の色で走っていたなめらかな移動を打ち切るためにも要る。
         枠がまだ出ていない(幅0の)ときは測れないので、形が決まってからもう一度そろえる。 */
      resetPhotoScroll(media);
      afterLayout(function () { if (curPhoto === 0) resetPhotoScroll(media); });
    }

    function buildSlide(it, k, multi) {
      var slide = document.createElement('div');
      slide.className = 'wc2c-sheet__slide';
      var src = it.ph.length ? photoUrl(it, k, 1200) : it.b;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wc2c-sheet__mediabtn';
      var alt = multi ? it.n + 'の写真 ' + (k + 1) : it.n + 'の色見本';
      btn.setAttribute('aria-label', alt + 'を大きく見る');
      if (src) {
        var im = document.createElement('img');
        var ss = photoSrcset(it, k);
        if (ss) {
          im.srcset = ss;
          /* 1枚の幅は枠の84%。PCはシートの中の片側 */
          im.sizes = '(min-width: 750px) 400px, 86vw';
        }
        im.src = src;
        im.alt = alt;
        im.decoding = 'async';
        if (k > 1) im.loading = 'lazy';
        btn.appendChild(im);
      }
      btn.addEventListener('click', function () {
        /* 色を送る横スワイプの直後に来た click では開かない */
        if (Date.now() - sheetSwipedAt < 500) return;
        openZoom(it, k);
      });
      slide.appendChild(btn);
      return slide;
    }

    /* 矢印の出し入れと、写真に被せる文字。
       被せる文字は1つだけ(何枚目か + 押すと大きくなること)。
       矢印は上下の真ん中、文字は左下なので重ならない。 */
    function syncPhotoNav() {
      var it = navSeq[cur];
      var n = it ? (it.ph.length || 1) : 0;
      var prev = $('[data-wc2c-ph-prev]');
      var next = $('[data-wc2c-ph-next]');
      var cap = $('[data-wc2c-ph-cap]');
      if (prev) prev.hidden = !(n > 1) || curPhoto <= 0;
      if (next) next.hidden = !(n > 1) || curPhoto >= n - 1;
      if (cap) {
        var how = isMobile() ? 'タップで拡大' : 'クリックで拡大';
        cap.textContent = n > 1 ? curPhoto + 1 + ' / ' + n + '・' + how : how;
        cap.hidden = n < 1;
      }
    }

    function renderPhotos(it) {
      if (!photosEl) return;
      photosEl.innerHTML = '';
      /* 写真が1枚だけの色ではサムネイルの列を出さない */
      photosEl.hidden = it.ph.length < 2;
      if (photosEl.hidden) return;
      placePhotos();
      it.ph.forEach(function (_, k) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'wc2c-photo';
        b.setAttribute('aria-label', '写真 ' + (k + 1) + ' を表示');
        b.setAttribute('aria-current', k === curPhoto ? 'true' : 'false');
        var im = document.createElement('img');
        im.src = photoUrl(it, k, 160);
        im.alt = '';
        im.decoding = 'async';
        b.appendChild(im);
        b.addEventListener('click', function () { showPhoto(k, 'thumb'); });
        photosEl.appendChild(b);
      });
    }

    /* k枚目を出す。how は切り替えの仕方('swipe' / 'arrow' / 'thumb')。
       スワイプのときは指でもう動いているので、こちらからは動かさない。 */
    function showPhoto(k, how) {
      var it = navSeq[cur];
      if (!it || k === curPhoto || !it.ph[k]) return;
      curPhoto = k;
      if (how !== 'swipe') scrollToPhoto(k);
      if (photosEl) {
        Array.prototype.forEach.call(photosEl.children, function (b, i) {
          b.setAttribute('aria-current', i === curPhoto ? 'true' : 'false');
        });
      }
      syncPhotoNav();
      track('wc2_color_photo_switched', { photo: k + 1, method: how || 'thumb' });
      say('写真 ' + (k + 1) + ' を表示しました');
      syncSheetActsSpace();
    }

    function resetPhotoScroll(media) {
      var first = media.querySelector('.wc2c-sheet__slide');
      var left = first ? photoSnapLeft(media, first) : 0;
      if (media.scrollTo) media.scrollTo({ left: left, behavior: 'auto' });
      else media.scrollLeft = left;
    }

    /* その1枚が枠の中央に来る位置。左右に同じだけ場所を空けてあるので、
       1枚目・最後の1枚もこの式で中央に止まる。 */
    function photoSnapLeft(media, slide) {
      return slide.offsetLeft + slide.offsetWidth / 2 - media.clientWidth / 2;
    }

    function scrollToPhoto(k) {
      var media = $('[data-wc2c-sheet-media]');
      var slide = media && media.querySelectorAll('.wc2c-sheet__slide')[k];
      if (!slide) return;
      var left = photoSnapLeft(media, slide);
      /* なめらかな移動は使わない。切り替えの直後に固定パネルの高さを測り直して
         枠の高さが変わることがあり、そのときの作り直しで scroll-snap が
         移動中の位置を元へ引き戻してしまう(実測: 矢印で送っても動かない)。 */
      if (media.scrollTo) media.scrollTo({ left: left, behavior: 'auto' });
      else media.scrollLeft = left;
    }

    /* 横スワイプで止まった位置から、いま何枚目かを読む。
       枠そのものは作り直さないので、結び付けは1回だけでよい。 */
    function bindPhotoScroll() {
      var media = $('[data-wc2c-sheet-media]');
      if (!media) return;
      var timer = null;
      media.addEventListener('scroll', function () {
        if (timer) clearTimeout(timer);
        timer = setTimeout(function () {
          var kids = media.querySelectorAll('.wc2c-sheet__slide');
          if (kids.length < 2) return;
          var best = 0;
          var bd = Infinity;
          for (var i = 0; i < kids.length; i++) {
            var d = Math.abs(photoSnapLeft(media, kids[i]) - media.scrollLeft);
            if (d < bd) { bd = d; best = i; }
          }
          if (best !== curPhoto) showPhoto(best, 'swipe');
        }, 120);
      }, { passive: true });
    }
    bindPhotoScroll();
    bindClick('[data-wc2c-ph-prev]', function () { showPhoto(curPhoto - 1, 'arrow'); });
    bindClick('[data-wc2c-ph-next]', function () { showPhoto(curPhoto + 1, 'arrow'); });

    /* スマホの拡大は「色見本を幅いっぱい」が既定。参考画像は切り替えで上下に並べる。
       PCは従来どおり、参考画像があれば常に左に並べる(挙動を変えない)。 */
    var ZOOM_REF_KEY = 'wigcut_color_zoomref_v1';
    var zoomRefShown = false;
    try { zoomRefShown = sessionStorage.getItem(ZOOM_REF_KEY) === '1'; } catch (e) {}

    function syncSheetRef() {
      var hasRef = !!ref.src;
      var mob = isMobile();
      if (sheetRefToggleEl) {
        sheetRefToggleEl.hidden = !(hasRef && mob);
        sheetRefToggleEl.textContent = zoomRefShown ? '参考画像を隠す' : '参考画像と並べる';
        sheetRefToggleEl.setAttribute('aria-pressed', zoomRefShown ? 'true' : 'false');
      }
      var show = hasRef && (mob ? zoomRefShown : true);
      var pane = $('[data-wc2c-sheet-refpane]');
      if (pane) pane.hidden = !show;
      if (sheetEl) sheetEl.classList.toggle('is-split', !!(mob && show));
      if (show) drawRefInto($('[data-wc2c-sheet-refcv]'));
      syncSheetActsSpace();
    }

    /* スマホでは色名・説明・操作をシートの下端に固定するので、
       本文の下にその高さぶんの場所を空けておく。空けないと画像の下に潜る。 */
    function syncSheetActsSpace() {
      if (!sheetEl) return;
      if (!isMobile()) { sheetEl.style.removeProperty('--wc2c-zinfo-h'); return; }
      var info = sheetEl.querySelector('.wc2c-sheet__info');
      if (!info) return;
      afterLayout(function () {
        var h = Math.round(info.getBoundingClientRect().height);
        if (h > 0) sheetEl.style.setProperty('--wc2c-zinfo-h', h + 16 + 'px');
      });
    }

    /* 色を開く。seq を渡すとその並びで前後に送る(渡さなければ今の並びのまま)。 */
    function openSheet(it, seq) {
      if (!it) return;
      if (seq && seq.length) navSeq = seq;
      if (!navSeq.length) navSeq = gridSeq();
      var i = navSeq.indexOf(it);
      if (i < 0) { navSeq = gridSeq(); i = navSeq.indexOf(it); }
      if (i < 0) { navSeq = [it]; i = 0; }
      showSheet(i);
    }

    function showSheet(i) {
      var it = navSeq[i];
      if (!it) return;
      cur = i;
      /* 色を送ったら写真は1枚目(毛束)に戻す */
      curPhoto = 0;
      setSheetMedia(it);
      renderPhotos(it);
      $('[data-wc2c-sheet-fam]').textContent = it.f;
      $('[data-wc2c-sheet-name]').textContent = it.n;
      $('[data-wc2c-sheet-desc]').textContent = it.d;
      $('[data-wc2c-sheet-pos]').textContent = i + 1 + ' / ' + navSeq.length;
      $('[data-wc2c-sheet-prev]').disabled = i === 0;
      $('[data-wc2c-sheet-next]').disabled = i === navSeq.length - 1;
      syncSheetPickBtn();
      if (!sheetOv.classList.contains('is-open')) {
        if (isMobile()) {
          /* スマホは全画面のシートと同じ作法(端末の戻るで閉じる・背面のスクロール停止) */
          sheetModal = modalOpen({
            ov: sheetOv,
            box: sheetEl,
            full: true,
            focus: $('[data-wc2c-sheet-close]'),
            onClose: function () { sheetModal = null; cur = -1; }
          });
        } else {
          sheetModal = null;
          lastFocus = document.activeElement;
          sheetOv.hidden = false;
          sheetOv.classList.add('is-open');
          document.body.style.overflow = 'hidden';
          hideFab(true);
          $('[data-wc2c-sheet-copy]').focus();
        }
      }
      syncSheetRef();
      track('wc2_color_zoom_opened', {});
    }

    function syncSheetPickBtn() {
      if (cur < 0 || !navSeq[cur]) return;
      var on = isPicked(navSeq[cur].n);
      var b = $('[data-wc2c-sheet-pick]');
      if (!b) return;
      b.textContent = on ? '候補から外す' : '候補に入れる';
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    function closeSheet() {
      if (sheetModal && modalStack[modalStack.length - 1] === sheetModal) { modalClose(); return; }
      sheetOv.classList.remove('is-open');
      sheetOv.hidden = true;
      document.body.style.overflow = '';
      hideFab(false);
      /* 追従ボタンを戻した**あと**に測り直す。閉じる前に測った値は
         シート表示中のものなので、ここで測らないと余白が残らない。 */
      syncTray();
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      cur = -1;
    }

    function step(d) {
      var n = cur + d;
      if (n < 0 || n >= navSeq.length) return;
      showSheet(n);
    }

    root.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-wc2c-open]') : null;
      /* グリッドから開いたときは、いま画面に並んでいる順で前後に送る */
      if (b && root.contains(b)) {
        openSheet(items[parseInt(b.getAttribute('data-wc2c-open'), 10)], gridSeq());
      }
    });
    bindClick('[data-wc2c-sheet-close]', closeSheet);
    bindClick('[data-wc2c-sheet-prev]', function () { step(-1); });
    bindClick('[data-wc2c-sheet-next]', function () { step(1); });
    bindClick('[data-wc2c-sheet-copy]', function () {
      if (navSeq[cur]) { copy(navSeq[cur].n, copiedMsg()); track('wc2_color_names_copied', { count: 1 }); }
    });
    bindClick('[data-wc2c-sheet-pick]', function () { if (navSeq[cur]) togglePick(navSeq[cur].n); });
    bindClick('[data-wc2c-sheet-reftoggle]', function () {
      zoomRefShown = !zoomRefShown;
      try { sessionStorage.setItem(ZOOM_REF_KEY, zoomRefShown ? '1' : '0'); } catch (e) {}
      track('wc2_color_ref_toggled', { shown: zoomRefShown ? 'on' : 'off' });
      syncSheetRef();
      say(zoomRefShown ? '参考画像を並べました' : '参考画像を隠しました');
    });
    if (sheetOv) {
      sheetOv.addEventListener('mousedown', function (e) { if (e.target === sheetOv) closeSheet(); });
    }

    if (sheetEl) {
      var sx0 = null;
      var sy0 = null;
      sheetEl.addEventListener('touchstart', function (e) {
        /* 写真の上から始めた横スワイプは「写真の送り」。色は送らない(取り違えを避ける) */
        var onMedia = e.target && e.target.closest && e.target.closest('[data-wc2c-sheet-media]');
        if (onMedia) { sx0 = null; return; }
        sx0 = e.touches[0].clientX; sy0 = e.touches[0].clientY;
      }, { passive: true });
      sheetEl.addEventListener('touchend', function (e) {
        if (sx0 === null) return;
        var dx = e.changedTouches[0].clientX - sx0;
        var dy = e.changedTouches[0].clientY - sy0;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          sheetSwipedAt = Date.now();
          step(dx < 0 ? 1 : -1);
        }
        sx0 = null;
      }, { passive: true });
    }

    /* ------------------------------- 写真を画面いっぱいに出す(2026-09-22)

       CSの指摘「毛束の画像が小さくて、全体が見えない」への対応。
       シートの中の枠は固定パネルの上に収まる大きさまでなので、
       じっくり見たい人のために、押すと画面全体に同じ写真を出す。

       参考画像(お客様の画像)の拡大 bindPickSurface とは共通化していない。
       あちらは canvas に描いて色を拾う仕組みで、役目も状態も違う。
       ここは img を transform で動かすだけ。 */

    var zoomOv = $('[data-wc2c-zoom-ov]');
    var zoomBox = $('[data-wc2c-zoom]');
    var zoomStage = $('[data-wc2c-zoom-stage]');
    var zoomModal = null;
    var zoomImg = null;
    var zv = { scale: 1, base: 1, tx: 0, ty: 0, w: 0, h: 0 };

    function zoomDraw() {
      if (!zoomImg) return;
      zoomImg.style.transform =
        'translate(' + zv.tx + 'px,' + zv.ty + 'px) scale(' + zv.scale + ')';
    }

    /* 画面に収まる大きさ・中央に置く(全体が見える状態) */
    function zoomFit() {
      if (!zoomImg || !zoomStage || !zv.w || !zv.h) return;
      var r = zoomStage.getBoundingClientRect();
      if (!r.width || !r.height) return;
      zv.base = Math.min(r.width / zv.w, r.height / zv.h);
      zv.scale = zv.base;
      zv.tx = (r.width - zv.w * zv.scale) / 2;
      zv.ty = (r.height - zv.h * zv.scale) / 2;
      zoomDraw();
    }

    /* 画面より小さいうちは中央。大きいときは端から中が見えないようにする */
    function zoomClamp() {
      if (!zoomStage) return;
      var r = zoomStage.getBoundingClientRect();
      var w = zv.w * zv.scale;
      var h = zv.h * zv.scale;
      zv.tx = w <= r.width ? (r.width - w) / 2 : Math.min(0, Math.max(r.width - w, zv.tx));
      zv.ty = h <= r.height ? (r.height - h) / 2 : Math.min(0, Math.max(r.height - h, zv.ty));
    }

    function zoomAtPt(vx, vy, k) {
      /* 読み込みが終わるまでは大きさが決まらない。決まる前は動かさない */
      if (!zv.w || !zv.h) return;
      var s0 = zv.scale;
      var s1 = Math.max(zv.base, Math.min(zv.base * 8, s0 * k));
      if (s1 === s0) return;
      zv.tx = vx - (vx - zv.tx) * (s1 / s0);
      zv.ty = vy - (vy - zv.ty) * (s1 / s0);
      zv.scale = s1;
      zoomClamp();
      zoomDraw();
    }

    function openZoom(it, k) {
      if (!zoomOv || !zoomStage || !it) return;
      var src = it.ph.length ? photoUrl(it, k, 1500) : it.b;
      if (!src) return;
      zoomStage.innerHTML = '';
      zv = { scale: 1, base: 1, tx: 0, ty: 0, w: 0, h: 0 };
      var im = document.createElement('img');
      im.alt = it.ph.length > 1 ? it.n + 'の写真 ' + (k + 1) : it.n + 'の色見本';
      im.decoding = 'async';
      im.addEventListener('load', function () {
        zv.w = im.naturalWidth;
        zv.h = im.naturalHeight;
        im.style.width = zv.w + 'px';
        im.style.height = zv.h + 'px';
        zoomFit();
      });
      im.src = src;
      zoomImg = im;
      zoomStage.appendChild(im);
      if (isMobile()) {
        /* スマホは他の全画面と同じ作法(端末の戻るで閉じる)。
           背面はシートがすでに止めているので、閉じても位置は動かない */
        zoomModal = modalOpen({
          ov: zoomOv,
          box: zoomBox,
          full: true,
          focus: $('[data-wc2c-zoom-close]'),
          onClose: function () {
            zoomModal = null;
            zoomStage.innerHTML = '';
            zoomImg = null;
          }
        });
      } else {
        zoomModal = null;
        zoomOv.hidden = false;
        zoomOv.classList.add('is-open');
        var c = $('[data-wc2c-zoom-close]');
        if (c && c.focus) c.focus();
      }
      /* 既存のイベント名を流用する。シートを開いたときと同じ「拡大を見た」行動なので
         別名を足さない。どちらかは view で分かるようにする */
      track('wc2_color_zoom_opened', { view: 'full' });
    }

    function closeZoom() {
      if (!zoomOv) return;
      if (zoomModal && modalStack[modalStack.length - 1] === zoomModal) { modalClose(); return; }
      zoomOv.classList.remove('is-open');
      zoomOv.hidden = true;
      if (zoomStage) zoomStage.innerHTML = '';
      zoomImg = null;
      var b = $('.wc2c-sheet__mediabtn');
      if (b && b.focus) { try { b.focus({ preventScroll: true }); } catch (e) { b.focus(); } }
    }

    bindClick('[data-wc2c-zoom-close]', closeZoom);

    if (zoomStage) {
      var zp = {};
      var zn = 0;
      var zmoved = false;
      var zsx = 0;
      var zsy = 0;
      var zpinch = null;
      var zlastTap = 0;

      var zpinchState = function () {
        var ks = Object.keys(zp);
        if (ks.length < 2) return null;
        var a = zp[ks[0]];
        var b = zp[ks[1]];
        var r = zoomStage.getBoundingClientRect();
        return {
          cx: (a.x + b.x) / 2 - r.left,
          cy: (a.y + b.y) / 2 - r.top,
          dist: Math.hypot(a.x - b.x, a.y - b.y)
        };
      };

      zoomStage.addEventListener('pointerdown', function (e) {
        if (!zoomImg) return;
        try { zoomStage.setPointerCapture(e.pointerId); } catch (err) {}
        zp[e.pointerId] = { x: e.clientX, y: e.clientY };
        zn++;
        if (zn === 1) { zmoved = false; zsx = e.clientX; zsy = e.clientY; }
        if (zn === 2) zpinch = zpinchState();
      });

      zoomStage.addEventListener('pointermove', function (e) {
        if (!zoomImg || !zp[e.pointerId]) return;
        var prev = zp[e.pointerId];
        zp[e.pointerId] = { x: e.clientX, y: e.clientY };
        if (zn === 1) {
          if (Math.abs(e.clientX - zsx) > 6 || Math.abs(e.clientY - zsy) > 6) zmoved = true;
          if (!zv.w || !zv.h) return;
          zv.tx += e.clientX - prev.x;
          zv.ty += e.clientY - prev.y;
          zoomClamp();
          zoomDraw();
        } else if (zn >= 2 && zpinch) {
          zmoved = true;
          var now = zpinchState();
          if (now && zpinch.dist > 0) { zoomAtPt(now.cx, now.cy, now.dist / zpinch.dist); zpinch = now; }
        }
      });

      var zendPtr = function (e) {
        if (!zp[e.pointerId]) return;
        delete zp[e.pointerId];
        zn = Math.max(0, zn - 1);
        if (zn < 2) zpinch = null;
        if (zn !== 0 || zmoved || !zoomImg) return;
        var now = Date.now();
        if (now - zlastTap < 320) {
          /* ダブルタップで拡大 / 全体に戻す */
          zlastTap = 0;
          var r = zoomStage.getBoundingClientRect();
          if (zv.scale > zv.base * 1.4) zoomFit();
          else zoomAtPt(e.clientX - r.left, e.clientY - r.top, 2.5);
        } else {
          zlastTap = now;
        }
      };
      zoomStage.addEventListener('pointerup', zendPtr);
      zoomStage.addEventListener('pointercancel', zendPtr);

      zoomStage.addEventListener('wheel', function (e) {
        if (!zoomImg) return;
        e.preventDefault();
        var r = zoomStage.getBoundingClientRect();
        zoomAtPt(e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY * 0.0016));
      }, { passive: false });

      window.addEventListener('resize', function () {
        if (zoomOv && zoomOv.classList.contains('is-open')) zoomFit();
      });
    }

    /* ----------------------------------------------------------- 比較 */

    var cmpOv = $('[data-wc2c-cmp-ov]');
    var cmpRow = $('[data-wc2c-cmp-row]');
    var cmpToolsEl = $('[data-wc2c-cmp-tools]');
    var cmpRefToggleEl = $('[data-wc2c-cmp-reftoggle]');
    var lastFocusCmp = null;

    /* 参考画像を隠せるようにする。既定は表示(これまでと同じ見え方)。
       選択は sessionStorage に覚える。 */
    var CMP_REF_KEY = 'wigcut_color_cmpref_v1';
    var cmpRefShown = true;
    try { cmpRefShown = sessionStorage.getItem(CMP_REF_KEY) !== '0'; } catch (e) {}

    function syncCmpTools() {
      if (cmpToolsEl) cmpToolsEl.hidden = !ref.src;
      if (cmpRefToggleEl) {
        cmpRefToggleEl.textContent = cmpRefShown ? '参考画像を隠す' : '参考画像を表示する';
        cmpRefToggleEl.setAttribute('aria-pressed', cmpRefShown ? 'true' : 'false');
      }
    }

    function renderCompare() {
      if (!cmpRow) return;
      syncCmpTools();
      cmpRow.innerHTML = '';
      var cells = [];
      if (ref.src && cmpRefShown) {
        var refCell = document.createElement('div');
        refCell.className = 'wc2c-cmp__cell';
        /* 参考画像を出すセルは Clarity の録画からマスクする(Liquid 側と同じ扱い) */
        refCell.setAttribute('data-clarity-mask', 'true');
        refCell.innerHTML =
          '<figure><div class="wc2c-cmp__ph"><canvas data-wc2c-cmp-refcv data-clarity-mask="true" ' +
          'role="img" aria-label="参考画像"></canvas></div>' +
          '<figcaption>参考画像</figcaption></figure>';
        cells.push(refCell);
      }
      picks.forEach(function (n) {
        var it = byName[n];
        var cell = document.createElement('div');
        cell.className = 'wc2c-cmp__cell';
        var fig = document.createElement('figure');
        var ph = document.createElement('div');
        ph.className = 'wc2c-cmp__ph';
        if (it && it.b) {
          var im = document.createElement('img');
          im.src = it.b;
          im.alt = n + 'の色見本';
          im.decoding = 'async';
          ph.appendChild(im);
        }
        var cap = document.createElement('figcaption');
        cap.textContent = n;
        fig.appendChild(ph);
        fig.appendChild(cap);
        cell.appendChild(fig);
        cells.push(cell);
      });
      cmpRow.style.setProperty('--wc2c-cmp-n', String(Math.max(2, cells.length)));
      cells.forEach(function (c) { cmpRow.appendChild(c); });
      if (ref.src && cmpRefShown) drawRefInto(cmpRow.querySelector('[data-wc2c-cmp-refcv]'));
    }

    function openCompare() {
      if (picks.length < 2) return;
      lastFocusCmp = document.activeElement;
      cmpOv.hidden = false;
      cmpOv.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      hideFab(true);
      renderCompare();
      $('[data-wc2c-cmp-close]').focus();
    }
    function closeCompare() {
      cmpOv.classList.remove('is-open');
      cmpOv.hidden = true;
      document.body.style.overflow = '';
      hideFab(false);
      syncTray();
      if (lastFocusCmp && lastFocusCmp.focus) lastFocusCmp.focus();
    }
    bindClick('[data-wc2c-compare]', openCompare);
    bindClick('[data-wc2c-cmp-close]', closeCompare);
    bindClick('[data-wc2c-cmp-copy]', function () {
      if (!picks.length) return;
      copy(picksText(), copiedMsg());
      track('wc2_color_names_copied', { count: picks.length });
    });
    bindClick('[data-wc2c-cmp-reftoggle]', function () {
      cmpRefShown = !cmpRefShown;
      try { sessionStorage.setItem(CMP_REF_KEY, cmpRefShown ? '1' : '0'); } catch (e) {}
      track('wc2_color_ref_toggled', { shown: cmpRefShown ? 'on' : 'off' });
      renderCompare();
      say(cmpRefShown ? '参考画像を表示しました' : '参考画像を隠しました');
    });
    bindClick('[data-wc2c-cmp-clear]', function () {
      picks = [];
      save();
      renderPicks();
      closeCompare();
    });
    if (cmpOv) {
      cmpOv.addEventListener('mousedown', function (e) { if (e.target === cmpOv) closeCompare(); });
    }

    /* -------------------------------------------------------- キーボード */

    document.addEventListener('keydown', function (e) {
      /* 写真の全画面は拡大シートの上に重なる。先に見る(Esc で下のシートを閉じない) */
      if (zoomOv && zoomOv.classList.contains('is-open')) {
        if (e.key === 'Escape') { e.preventDefault(); closeZoom(); }
        else if (e.key === 'Tab') trap(e, zoomBox);
      } else if (sheetOv && sheetOv.classList.contains('is-open')) {
        if (e.key === 'Escape') { e.preventDefault(); closeSheet(); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
        else if (e.key === 'Tab') trap(e, sheetEl);
      } else if (cmpOv && cmpOv.classList.contains('is-open')) {
        if (e.key === 'Escape') { e.preventDefault(); closeCompare(); }
        else if (e.key === 'Tab') trap(e, $('[data-wc2c-cmp]'));
      } else if (modalStack.length) {
        var top = modalStack[modalStack.length - 1];
        if (e.key === 'Escape') { e.preventDefault(); modalClose(); }
        else if (e.key === 'Tab') trap(e, top.box);
      }
    });

    function trap(e, box) {
      if (!box) return;
      /* 隠れている操作要素(参考画像の切り替えなど)はフォーカスの輪に入れない */
      var f = Array.prototype.filter.call(
        box.querySelectorAll('button:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])'),
        function (el) { return el.offsetWidth > 0 || el.offsetHeight > 0; }
      );
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ---------------------------------------------------------- 仕上げ */

    function bindClick(sel, fn) {
      var el = $(sel);
      if (el) el.addEventListener('click', fn);
    }

    renderPicks();
    renderRefRow();
    watchFab();
    remeasureFabLater();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-wc2c-root]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* テーマエディタでのセクション再描画にも対応する */
  document.addEventListener('shopify:section:load', boot);
})();
